import { useState, useEffect, useCallback, useMemo } from "react";
import { useSession } from "@clerk/clerk-react";
import {
  Layers,
  Globe,
  GraduationCap,
  Send,
  Target,
  Mic,
  Phone,
  Megaphone,
  NotebookPen,
  Calendar,
  Mail,
  Archive,
  Settings,
} from "lucide-react";
import { BRAND } from "../constants/colors";
// Pure reads only. Offers themselves now live in Supabase and arrive as the
// `offerStore` prop, owned by App so the sidebar's count and the ladder can
// never disagree — see src/utils/useOffers.js.
import { hasSignatureOffer, EMPTY_PRICING } from "../utils/offerParse";

import { ChromeBar } from "./ChromeBar";
import { ConfirmDialog } from "./ConfirmDialog";
// Shared with SocialGenerator — see BrowseTable.jsx for why these use CSS vars
// directly rather than this file's `B` map.

import { useCtas } from "../utils/useCtas";
import { deriveTitle, useSocialPosts } from "../utils/useSocialPosts";
import { postDay, postHasWords, postState } from "../utils/socialPosts";
import { useAutoLearn } from "../utils/autoLearn";
import { PostEditor, SocialCopy, SocialTable } from "./momentum/social";
import { ArcScreen, ArcSetup } from "./momentum/socialArc";
import { addDays, arcContext, arcRows, dayLabel, useSocialArcs, withSlotDay } from "../utils/socialArcs";
import { useWebsitePages, pagesOfSite, contextScopeFor, slugify, standardPagesFor, nextOrder, WEBSITE_SOURCE } from "../utils/websitePages";
import { dropDeadIds, landingSiteFor, offersPointingAt, pointAt, supportersOf } from "../utils/offerDestination";
import { offerForPage } from "../utils/pageOffer";
import { useSites, LANDING_SITE, SITE_KINDS, siteKind, siteLabel } from "../utils/useSites";
import { useCourses, courseLabel } from "../utils/useCourses";
import { useWebsiteSections, sectionHeading, startSection, pageState } from "../utils/websiteSections";

import { useCourseModules, modulesOfCourse } from "../utils/useCourseModules";
import { useOutreach, sequenceLabel, touchWords, roleDef, parseTouchBlock, applyTouchBlock, touchKindOf } from "../utils/outreach";
import { blockContent, withBlockContent, blockKind, blockWords, blocksOf } from "../utils/courseBlockKinds";
import { parseCourseBlock, applyCourseBlock } from "../utils/courseBlock";

/* Split out 2026-08-20 — see momentum/ui.jsx. This file keeps the state every
   panel shares and the routing between them; the panels and the parts they are
   built from live beside it. */
import { SiteTable, WebsitePage, WebsitePageCopy } from "./momentum/website";
import { sectionDraftReply } from "./momentum/sections";
import { SectionStatus } from "./momentum/fields";
import { applySectionBlock, parseSectionBlock } from "../utils/sectionBlock";
import { hasContent, kindDef } from "../utils/websiteSectionKinds";
import { OfferLadder, OfferCopy, OfferForm, groupOffers, offerReorderWrites } from "./momentum/offers";
import { CtaCopy, CtaLibrary, CtaForm } from "./momentum/ctas";
import {
  CourseShell, CoursesTable, CourseCopy, CourseModule, CourseDrafter,
  courseDraftReply, MODULE_BACK,
} from "./momentum/course";
import { OutreachShell, OutreachTable, OutreachCopy, OutreachSequence, OutreachDrafter, touchDraftReply, TOUCH_BACK } from "./momentum/outreach";
import { Library } from "./momentum/library";
import { rungTag } from "../utils/offerRung";
import { CallScriptsShell, CallScriptsTable, CallScriptsCopy, CallScript, CallScriptDrafter, scriptDraftReply, scriptBackLabel } from "./momentum/callScripts";
import { NewsletterShell, NewsletterTable, NewsletterCopy, NewsletterPiece, NewsletterDrafter, newsletterDraftReply, PIECE_BACK } from "./momentum/newsletter";
import {
  useNewsletters, groupNewsletters, newsletterLabel, isSource, isWritingPiece,
  parseNewsletterBlockReply, applyNewsletterBlockReply,
  /* Aliased: `blockWords` and `blockName` already mean a COURSE block above. */
  blockName as nlBlockName, blockWords as nlBlockWords,
} from "../utils/newsletters";
import { parsePillars, pillarLabels } from "../utils/pillars";
import { brandSkin } from "../utils/brand";
import { useCallScripts, groupScripts, scriptLabel, sectionName, sectionWords, parseScriptSectionBlock, applyScriptSectionBlock, isVideo } from "../utils/callScripts";
import { useVoiceRecorder } from "../utils/voiceDrafts";
import {
  PresentationsShell, PresentationsTable, PresentationsCopy, PresentationDeck, PresentationDrafter, proposeSlides, deckDraftReply, DECK_BACK,
} from "./momentum/presentations";
import { previewBlogPost, publishToBlog } from "../utils/blogPublish";
import { newsletterShapeReply } from "../utils/newsletterShape";
import { NewsletterWriter } from "./momentum/newsletterWriter";
import { usePresentations, presentationLabel, slideWords, slideKind, buildSlideContext, parseSlideBlock, applySlideBlock } from "../utils/presentations";

import { B, pTitle, pDesc, soonCard, nb } from "./momentum/tokens";
import { L1Settings, L1Switcher, useRecordScreen } from "./momentum/shell";
import { Bread, Back, CogNudge, IconBtn, Note, PageTitleField, Status, SoonBadge, RunOverview, SocialNote, useTwoPane } from "./momentum/ui";




/* ── Structure: the nav IS the funnel (2026-08-22) ────────────────────────────
   status: "live" | "soon" | "ext". multi: shows the little + affordance.

   `color` is read by the Sidebar, which is CHROME and always dark — so these
   are fixed brand values, never canvas tokens. A `var(--accent)` here would
   resolve to the light-canvas teal on the indigo sidebar.

   Build stands apart — it is where capability gets MADE, and none of it belongs
   to a stage. An offer isn't "an Awareness offer"; it is raw material the other
   three sections point at. Grow / Run are the funnel, walked in
   order: Awareness → Interest + Conversion (Grow) → Loyalty (Run). That is why the Funnel
   module could go away without losing anything — the funnel was never missing,
   it was living as a screen instead of as the structure it already was.

   IDS NEVER MOVE, ONLY LABELS AND PLACES. `id` is the route (`m/<id>`), the
   `panel` prop, and the SOON_COPY key — so `ctas` stays `ctas` while reading
   "Meeting Links", the same way OFFER_TYPES keeps `core` under a renamed label
   and website_pages keeps `source: "accelerator"`. Renaming an id here would
   break every stored route for a cosmetic win.                                */
export const BUCKETS = [
  { key: "build", label: "Build", color: BRAND.tealLight, items: [
    { id: "offer_ladder", icon: Layers, label: "Offer Ladder", multi: true, status: "live" },
    // "Sites" ahead of the Site → Page → Block work, her call: the nav names the
    // architecture it is heading for. The panel is still one flat list of pages.
    { id: "website", icon: Globe, label: "Sites", multi: true, status: "live" },
    // Moved out of Attract. A talk is a thing you MAKE and then point at from
    // anywhere — a kickoff deck referenced by an onboarding sequence is the same
    // object as a masterclass deck. That is Build's definition, not Attract's.
    /* "admin": live for Tevra only, "coming soon" for everyone else, while it
       is tested (her call, 2026-09-16 — "one of the more complicated needing
       testing"). Flip to "live" to release it. See `toolStatus`. */
    { id: "talks", icon: Mic, label: "Presentations", multi: true, status: "admin" },
    /* NO `route`. A route sends the sidebar straight to the module CHAT
       (`toolNavId` in Sidebar.jsx is `it.route || "m/" + it.id`), which is what
       this was when Course had no panel of its own. With one, it navigates like
       every other Build tool — `m/lesson_plan` → the panel below — and the chat
       is opened from inside it, exactly as Website opens My Website. */
    { id: "lesson_plan", icon: GraduationCap, label: "Courses", status: "live" },
  ]},
  /* GROW — how she gets clients, 2026-09-17. Was two groups, Attract
     (awareness) and Connect (interest + conversion), and she kept not knowing
     which one to open: "I keep not knowing which one to click." The funnel did
     not go anywhere. ORDER, her call the same day: the one-to-one tools first
     (Outreach, Call Scripts), then the one-to-many generators. The keys are React keys and
     expand state only — nothing stores them. Amber is Attract's line colour,
     carried over. */
  /* ONE WORD EACH (her call, 2026-09-17, reading the Newsletter mockup: "I
     like the nav it says outreach, scripts, social, newsletters, Ads rather
     than the current nav is two words — I think it's cleaner"). "Generator"
     and "Sequences" said nothing a coach needed: every row in a nav is a tool.
     LABELS ONLY — ids are routes and never move, so `salescall` still reads
     Scripts and `outbound` still reads Outreach. The panels' own headings
     ("Scripts", "Outreach Sequences") are their own: the nav is a list to
     scan, a heading is the name of the thing. */
  { key: "grow", label: "Grow", color: BRAND.amber, items: [
    { id: "outbound", icon: Send, label: "Outreach", multi: true, status: "live" },
    /* Live 2026-09-16 as "Call Scripts" (docs/call-scripts-spec.md). The id
       stays `salescall` — ids never move, only labels. */
    { id: "salescall", icon: Phone, label: "Scripts", multi: true, status: "live" },
    /* CONTENT PLAN IS OFF THE NAV (her call, 2026-09-17): it is to be rolled
       INTO the Social Generator, not kept as a door of its own. Nothing else
       was removed — the module, its prompt chain (api/_lib/prompts.js), its three
       locked sections (constants/modules.js) and its `content_studio` route all
       stay, and `content_studio` is still in MOMENTUM_MODULES, so an old link
       still lands inside Momentum. To put the door back, re-import `Newspaper`
       from lucide-react and restore this row here:
       { id: "content_plan", icon: Newspaper, label: "Content Plan", status: "live", route: "content_studio" } */
    { id: "social", icon: Megaphone, label: "Social", status: "live", route: "social_generator" },
    { id: "newsletter", icon: Mail, label: "Newsletters", multi: true, status: "live" },
    { id: "ads", icon: Target, label: "Ads", multi: true, status: "soon" },
  ]},
  /* RUN — running the business day to day (her framing, 2026-09-17): the
     links clients book through, admin, and the Library where she catches
     things as they happen. Onboarding and Client Success are not modules:
     onboarding is a TYPE of outreach sequence, and what it sends is a
     Presentation. Analytics and Tasks are ideas, not rows — the sidebar lists
     only what exists (her rule, same day).

     Salmon, the line Connect used to carry (her call, 2026-09-17). Only ever a
     decorative line here, never text. */
  { key: "run", label: "Run", color: BRAND.salmon, items: [
    // Was "CTAs" under the old Attract group. Same records, same table, same `offers.cta_id` —
    // a booking link is what a CTA always was, named for what it is and filed
    // where it is used.
    { id: "ctas", icon: Calendar, label: "Meeting Links", multi: true, status: "live" },
    { id: "admin", icon: Archive, label: "Admin", status: "soon" },
    /* The notepad: ideas, stories (hers and clients'), testimonials,
       objections and, once GHL is connected, images — jotted in seconds, no
       chat, picked from by the tools above (spec: the "Library + sidebar
       restructure" doc, 2026-09-17). It replaces the "Story Inventory" row that
       sat under Connect: stories are one kind inside it, not a tool of their
       own. Filed under Run because this is where she catches things while
       running the business. */
    { id: "library", icon: NotebookPen, label: "Library", status: "live" },
  ]},
];

/** The sidebar's id for a Momentum panel — the nav row's `route`, else
 *  `m/<id>`, as Sidebar's toolNavId builds it — or null for a panel the nav
 *  does not list. App reads it back as `panel` (social_generator → social,
 *  m/<id> → <id>). */
export function navIdFor(panelId) {
  const item = BUCKETS.flatMap((bk) => bk.items).find((it) => it.id === panelId);
  return item ? item.route || `m/${item.id}` : null;
}

/* The one admin account — moved to utils/admin.js so small components can
   ask without importing the hub; re-exported so existing imports still work. */
export { ADMIN_EMAIL, isAdminEmail } from "../utils/admin";

/**
 * Is this tool live FOR THIS PERSON? "live" is live for everyone; "admin" is
 * live for the admin account and "soon" for everyone else, so an unfinished
 * tool can be tested on production behind the coming-soon screen. The sidebar
 * (the row, the tick, the progress bar) and the hub (which screen renders) both
 * ask here, so they cannot disagree about it.
 */
export function toolStatus(item, isAdmin) {
  if (item?.status === "admin") return isAdmin ? "live" : "soon";
  return item?.status || "soon";
}


/* `outbound` is gone from here — Outreach Sequences is live. Its old blurb said
   "email sequences", which the built panel supersedes: a sequence is one
   platform of four, and LinkedIn is the default rather than email. */
const SOON_COPY = {
  talks: ["Build", "Signature talks, masterclasses, kickoff decks — one core script and its slides, repurposed per room and pointed at from anywhere that needs it."],
  // `story` was a row until 2026-09-17; kept so an old m/story route still reads.
  story: ["Run", "Stories now live in the Library, beside ideas, testimonials and objections."],
  ads: ["Grow", "Paid versions of what already works, tagged by who they are for — so a cold-audience ad and a warm retargeting ad are never the same copy."],
  lesson_plan: ["Build", "Built here, and delivered to the people an offer brings in. Every module is a block you write once and reuse."],
};

// ── Component ─────────────────────────────────────────────────────────────────
export function MomentumHub({
  isMobile = false, onMenuOpen, onOpenModule, panel,
  courseSeeded = false, markCourseSeeded, settingsLoaded = false,
  websiteOutputs = {}, offerOutputs = {},
  // For the Offer Ladder's drafting chat: her full locked Accelerator record, plus
  // what's needed to label sections built on a niche she has since left.
  acceleratorOutputs = null, activeNiche = null, outputStamps = null, userSettings = {},
  // The whole `useOffers()` return, owned by App. Passed as one object rather
  // than eight props, and shared with Sidebar so its Momentum count can't drift
  // from what the ladder shows.
  offerStore = {},
  // The admin account sees tools whose status is "admin" (see toolStatus).
  isAdmin = false,
  /* Social (panel "social"): { clientContext, contentPillars } from App's
     buildSocialContext — the brand foundation Brill writes posts from. */
  socialContext = null,
}) {
  const [active, setActive] = useState(panel || "offer_ladder");
  /* Voice learning by itself, once a visit to Social (utils/autoLearn.js) —
     the server spends nothing unless ten new edits wait and a week has passed. */
  useAutoLearn("social_generator", active === "social");
  const [menuOpen, setMenuOpen] = useState(false);
  /* Sites' phone screens: the list of pages, or one page. Held HERE rather than
     inside RecordCopy because the page's footer back is rendered from this file
     too, and the two have to agree about where "back" goes. `useTwoPane` and
     not `isMobile`: the shell splits at 1080 and App calls mobile at 768, and a
     footer told by the wrong number would send her somewhere else across 300px
     of width. */
  const pageScreen = useRecordScreen();
  const twoPaneHub = useTwoPane();
  /* CTAs. Owned here rather than in App like offers, because nothing outside
     Momentum reads them yet — lift it when the Social Generator starts pointing
     posts at real CTAs instead of the two generic options it has today. */
  const {
    rows: ctas, loading: ctasLoading, error: ctasError,
    save: persistCta, remove: removeCta, clearError: clearCtasError, newId: newCtaId,
  } = useCtas();
  const [editingCta, setEditingCta] = useState(null);
  const [savingCta, setSavingCta] = useState(false);
  /* Meeting Links went through the screen pattern 2026-09-23, shaped like the
     Offer Ladder: a Table / Link toggle over ONE editing screen, where there
     used to be a separate `cta_form` screen with Save and Cancel. */
  const [ctaView, setCtaView] = useState("table");

  /* The id is made HERE, at Add, so the first field she leaves writes the row
     and every later one updates it — the store upserts on id. Nothing is
     written until she has typed a name or button text (see CtaForm). */
  const startAddCta = (type) => {
    setEditingCta({ id: newCtaId(), name: "", buttonText: "", description: "", link: "", type, status: "draft", isNew: true });
    setMenuOpen(false);
    setCtaView("copy");
  };
  const openCta = (c) => { setEditingCta({ ...c }); setCtaView("copy"); };
  /* Pressing `Link` with nothing picked opens the first link, never an empty
     form — the same fallback goOfferView makes, for the same report. */
  const goCtaView = (v) => {
    if (v === "copy" && !editingCta && ctas[0]) setEditingCta({ ...ctas[0] });
    setCtaView(v);
  };

  /* `patch` is how a CHOICE saves — see saveOffer, whose shape this is. Stays
     on the link: the form saves on the way out of every field now, so leaving
     the screen on a save would throw her out on the first blur. */
  const saveCta = async (patch) => {
    if (!editingCta) return;
    const { isNew, ...c } = { ...editingCta, ...(patch || {}) };
    /* Most blurs have nothing to write — the same guard saveOffer carries. */
    const stored = ctas.find((x) => x.id === c.id);
    if (!isNew && stored && JSON.stringify({ ...stored, updatedAt: 0 }) === JSON.stringify({ ...c, updatedAt: 0 })) return;
    setSavingCta(true);
    const saved = await persistCta(c);
    setSavingCta(false);
    if (!saved) return;   // failed → the banner shows, the words stay on screen
    setEditingCta((d) => (d && d.id === c.id ? { ...saved } : d));
  };

  const askDeleteCta = (c) => setConfirm({
    title: "Delete this CTA?",
    message: `"${c.name || c.buttonText || "Untitled CTA"}" will be removed. Anything pointing at it will lose its link.`,
    confirmLabel: "Delete",
    danger: true,
    onConfirm: async () => {
      const ok = await removeCta(c.id);
      setConfirm(null);
      if (!ok) return;
      /* Postgres nulls `offers.cta_id` for us (on delete set null), but our copy
         of those offers still carries the dead id — the ladder would keep naming
         a CTA that no longer exists, and saving one would fail the foreign key.
         Only reload when something actually pointed at it. */
      // Read through `offerStore` (a prop) rather than the `offers` const, which
      // is destructured further down this component — no use-before-define.
      if ((offerStore.offers || []).some((o) => o.ctaId === c.id)) offerStore.reload?.();
      setEditingCta(null); setCtaView("table"); go("ctas");
    },
  });
  /* Website pages. Owned here for the same reason as CTAs — nothing outside
     Momentum reads them. Unlike offers there's no seeded flag to thread through
     App: `website_pages` is unique on (clerk_user_id, slug), so the natural key
     does that job. See utils/websitePages.js. */
  /* ── LEVEL 1 ──────────────────────────────────────────────────────────────
     Her sites and her courses (spec 8.5, decisions 6.3 and 6.8). Both tables
     come from supabase/0015, whose backfill gave every account that had pages
     or modules exactly one — so `sites[0]` is "My site" and every page she has
     is already in it. Nothing else in the nav gets one: Outreach earns no
     container, and a dropdown with one item in it is a promise of somewhere
     else to go.

     The selection is LOCAL STATE, not a route and not a stored preference.
     Which site she was last looking at is not a fact about her business, and a
     remembered one is a screen that opens somewhere she did not choose. It
     falls back to the first whenever it is empty or points at one she has since
     deleted — the same rule `copyPage` and `copyModule` follow. */
  const {
    sites, loading: sitesLoading, error: sitesError, clearError: clearSitesError,
    saveSite, deleteSite, addSite, reorderSites,
  } = useSites();
  const [openSiteId, setOpenSiteId] = useState(null);
  const [siteCog, setSiteCog] = useState(false);
  const site = sites.find((x) => x.id === openSiteId) || sites[0] || null;

  const {
    courses, loading: coursesLoading, error: coursesError, clearError: clearCoursesError,
    saveCourse, deleteCourse, addCourse, reorderCourses,
  } = useCourses();
  const [openCourseId, setOpenCourseId] = useState(null);
  const [courseCog, setCourseCog] = useState(false);
  const course = courses.find((c) => c.id === openCourseId) || courses[0] || null;

  const {
    pages, allPages, loading: pagesLoading, error: pagesError, clearError: clearPagesError,
    savePage: persistPage, deletePage: persistDeletePage, newPageId,
    addStandardPage, reorderPages, nextPageOrder, missing: missingPages, standardPages,
  } = useWebsitePages({
    websiteOutputs,
    siteId: site?.id || null,
    siteKind: siteKind(site),
    /* Only the FIRST site adopts pages that have no site of their own — see the
       note on `pages` in websitePages.js. Showing them on every site would list
       one page twice; showing them on none would lose it silently. */
    adoptUnsited: !!site && sites[0]?.id === site.id,
  });
  /* Her COURSE: modules seeded once from the Curriculum she locked in My Offer.
     The curriculum stays there — it's what she markets from — so this pulls a
     copy and never writes back. See utils/useCourseModules.js. */
  const {
    modules: courseModules, allModules, loading: courseLoading, error: courseError, clearError: clearCourseError,
    saveModule, deleteModule, addModule, reorderModules, addBlock, removeBlock,
    pullCurriculum, canPull: canPullCurriculum, missingCount: missingModules,
  } = useCourseModules({
    offerOutputs,
    courseId: course?.id || null,
    adoptUncoursed: !!course && courses[0]?.id === course.id,
    courseSeeded,
    markSeeded: markCourseSeeded,
    // The pull stays off until the flag has been read, or it runs against an
    // unknown state and can pull her whole curriculum twice.
    enabled: settingsLoaded,
  });
  /* WHAT EACH SITE AND EACH COURSE HOLDS, for the two landing tables.
     `adoptUnsited` / `adoptUncoursed` is true only for the FIRST record, which
     is the same rule the open site's own list follows — a page written before
     sites existed belongs to exactly one of them, and counting it on every site
     would show one page twice. */
  /* Every page an offer may point at. Deleting a site takes its pages in the
     database (on delete cascade) but not in our copy of them until a reload, so
     a page whose site is gone is left out rather than offered and then refused
     by the foreign key. A page with no site is the first site's (see above). */
  /* HER SOCIAL POSTS. Read here for "Supported by" on an offer and, since
     2026-09-24 (spec phase A1), written here too: Social is a Momentum panel
     now, on the same ADD → save → edit pattern as Meeting Links. */
  const {
    rows: socialPosts, loading: postsLoading, error: postsError,
    save: persistPost, remove: removePost, clearError: clearPostsError, newId: newPostId,
  } = useSocialPosts();
  const [editingPost, setEditingPost] = useState(null);
  const [savingPost, setSavingPost] = useState(false);
  const [postView, setPostView] = useState("table");
  /* The id is made at Add, so the first field she leaves writes the row and
     every later one updates it. Nothing is written until the post has words
     (postHasWords) — an empty row would sit in her post history as nothing. */
  const startAddPost = () => {
    setEditingPost({ id: newPostId(), title: "", platform: "linkedin", caption: "", firstComment: "", inputs: {}, status: "draft", isNew: true });
    setPostView("copy");
  };
  const openPost = (p) => { setEditingPost({ ...p }); setPostView("copy"); };
  /* `whole` is the post as the editor holds it at the moment she left a field
     — passed in rather than read from `editingPost`, whose last render can be a
     keystroke behind (the race found on Me → Voice the same day). */
  const savePost = async (whole) => {
    const { isNew, ...p } = { ...editingPost, ...(whole || {}) };
    if (!postHasWords(p)) return;
    /* A post with no name is named from its first line, as the old screen did
       on its first save — only while the name is empty, so hers is never
       overwritten. Without it, a post she writes herself reads "Untitled post"
       in the table forever. */
    const named = !String(p.title || "").trim() && String(p.caption || "").trim();
    if (named) p.title = deriveTitle(p.caption);
    const stored = socialPosts.find((x) => x.id === p.id);
    if (!isNew && stored && JSON.stringify({ ...stored, updatedAt: 0 }) === JSON.stringify({ ...p, updatedAt: 0 })) return;
    setSavingPost(true);
    const saved = await storePost(p);
    setSavingPost(false);
    if (!saved) return;   // failed → the banner says so, the words stay on screen
    /* An arc's post moved to another day (its date field, or the send) moves
       in the arc's plan too, or the Arcs tab and the post disagree. */
    if (p.arcId && p.inputs?.arcSlot) {
      const arc = socialArcs.find((a) => a.id === p.arcId);
      const moved = arc && withSlotDay(arc, Number(p.inputs.arcSlot), postDay(p));
      if (moved && moved !== arc) persistArc(moved);
    }
    /* Only what the save decided comes back into the editor — never the whole
       row, which would overwrite anything she typed while it was in flight. */
    setEditingPost((d) => (d && d.id === p.id
      ? { ...d, isNew: false, updatedAt: saved.updatedAt, createdAt: saved.createdAt, ...(named && !String(d.title || "").trim() ? { title: p.title } : {}) }
      : d));
  };
  /* THE ONE WRITE OF A POST, for the post screen and an arc's rows alike. A
     post with no name is named from its first line; a foreign key to an offer
     or link deleted while the post was open would fail the whole save, so a
     dead id is dropped rather than sent. */
  const storePost = async (post) => {
    const { isNew, ...p } = post;
    if (!String(p.title || "").trim() && String(p.caption || "").trim()) p.title = deriveTitle(p.caption);
    // `offerStore`, not `offers`: that const is destructured further down.
    if (p.offerId && !(offerStore.offers || []).some((o) => o.id === p.offerId)) p.offerId = null;
    if (p.ctaId && !ctas.some((c) => c.id === p.ctaId)) p.ctaId = null;
    return persistPost(p);
  };

  /* SOCIAL ARCS (2026-09-28): a series where each post builds on the one
     before. The arc's own screen lives in momentum/socialArc.jsx; this holds
     only the store and which arc is open. `autoPlanArcId` is the arc just set
     up — its screen plans it once, on arrival. */
  const {
    rows: socialArcs, loading: arcsLoading, error: arcsError,
    save: persistArc, remove: removeArc, clearError: clearArcsError, newId: newArcId,
  } = useSocialArcs();
  const [openArcId, setOpenArcId] = useState(null);
  const [arcSetupOpen, setArcSetupOpen] = useState(false);
  const [creatingArc, setCreatingArc] = useState(false);
  const [autoPlanArcId, setAutoPlanArcId] = useState(null);
  const openArc = (a) => { setOpenArcId(a.id); setEditingPost(null); setPostView("arc"); };
  const createArc = async (s) => {
    setCreatingArc(true);
    const saved = await persistArc({
      id: newArcId(), name: "", platform: s.platform, startDate: s.start, endDate: addDays(s.start, s.days - 1),
      status: "draft", themeMix: s.themeMix,
      offerId: s.offerId && (offerStore.offers || []).some((o) => o.id === s.offerId) ? s.offerId : null,
      /* `library` and `pictures`: what she picked and added at setup (socialArcs.js). */
      brief: { about: s.about, days: s.days, count: s.count, ask: s.ask, length: s.length, library: s.library || [], pictures: s.pictures || [] },
    });
    setCreatingArc(false);
    if (!saved) return;   // the pop-up stays open with her answers, and says why
    setArcSetupOpen(false);
    setAutoPlanArcId(saved.id);
    openArc(saved);
  };
  const askDeleteArc = (a) => {
    const kept = socialPosts.filter((p) => p.arcId === a.id && String(p.caption || "").trim()).length;
    setConfirm({
      title: "Delete this arc?",
      message: `"${a.name || "Untitled arc"}" and its plan will be deleted. ${kept ? `Its ${kept} written post${kept === 1 ? "" : "s"} stay${kept === 1 ? "s" : ""}, as single posts.` : "It has no written posts."} There's no undo.`,
      confirmLabel: "Delete arc",
      danger: true,
      onConfirm: async () => {
        const ok = await removeArc(a.id);
        setConfirm(null);
        if (ok && openArcId === a.id) { setOpenArcId(null); setPostView("table"); }
      },
    });
  };

  /* Done is her mark (rule 6), from the rail's dot or anywhere else. */
  const markPost = async (post, status) => {
    const saved = await persistPost({ ...post, status });
    if (saved) setEditingPost((d) => (d && d.id === post.id ? { ...d, status } : d));
  };
  const askDeletePost = (p) => setConfirm({
    title: "Delete this post?",
    message: `"${p.title || "Untitled post"}" and its first comment will be deleted. There's no undo.`,
    confirmLabel: "Delete",
    danger: true,
    onConfirm: async () => {
      const ok = await removePost(p.id);
      setConfirm(null);
      if (ok && editingPost?.id === p.id) { setEditingPost(null); setPostView("table"); }
    },
  });

  const offerablePages = useMemo(
    () => allPages.filter((p) => !p.siteId || sites.some((x) => x.id === p.siteId)),
    [allPages, sites],
  );
  const pagesOf = useCallback(
    (siteId) => pagesOfSite(allPages, siteId, sites[0]?.id === siteId),
    [allPages, sites],
  );
  const modulesOf = useCallback(
    (courseId) => modulesOfCourse(allModules, courseId, courses[0]?.id === courseId),
    [allModules, courses],
  );

  /* Her OUTREACH sequences, and the three touches inside each. Owned here like
     CTAs and pages — nothing outside Momentum reads them. Two tables, both
     loaded whole: the table shows a word count and a state per row, so fetching
     per sequence would be a request inside a render loop. */
  const {
    sequences, loading: outreachLoading, error: rawOutreachError, clearError: clearOutreachError,
    touchesFor, saveSequence, deleteSequence, addSequence, reorderSequences,
    saveTouch, ensureTouches, addTouch, removeTouch, reorderTouches,
  } = useOutreach();
  /* Until supabase/0019 runs, a sequence has no `type` column, and adding one is
     refused with a sentence about a column. Said in words she can act on. */
  const outreachError = rawOutreachError && /'type' column|column .*type/i.test(rawOutreachError)
    ? "Sequence types need a database update first: supabase/0019_outreach_type.sql."
    : rawOutreachError;
  const [outreachView, setOutreachView] = useState("table");
  const [openSequenceId, setOpenSequenceId] = useState(null);
  /* Which touch she is writing with Brill, by id — looked up in the current
     list, so deleting or clearing never leaves the chat on a row that moved. */
  const [draftingTouchId, setDraftingTouchId] = useState(null);
  /* THE ONE-PRESS WRITE ON A TOUCH (her report, 2026-09-17: "outreach sequences
     has no magic button to write the touch points"). Sites and Course have had
     it since they were built; Outreach only ever had the chat. Same three
     pieces as those two: who is busy, the confirm before a rewrite, and the
     stamp that remounts the row so the words show without opening the chat. */
  const [sparklingTouchId, setSparklingTouchId] = useState(null);
  const [outreachDraftError, setOutreachDraftError] = useState(null);
  const [draftedTouch, setDraftedTouch] = useState(null);

  const runTouchSparkle = async (sequence, touch, touches) => {
    const kind = touchKindOf(touch);
    setSparklingTouchId(touch.id);
    setOutreachDraftError(null);
    try {
      const token = await session?.getToken();
      const reply = await touchDraftReply({
        token, sequence, touch, touches, offers, chatContext: momentumChatContext,
        messages: [{
          role: "user",
          content: `Draft this ${roleDef(touch?.touchRole)?.label?.toLowerCase() || "touch"} from what I've locked, and put it straight in the fields. No questions first — draft it, and I'll tell you what to change.`,
        }],
      });
      const block = parseTouchBlock(reply, touch);
      if (!block || !Object.keys(block).length) {
        /* A reply with no block is Brill asking a question rather than writing.
           Saying so beats a sparkle that stops with nothing different. */
        setOutreachDraftError("Brill didn't have enough to draft that touch — open the chat on it and say what it has to do.");
        return;
      }
      const before = { subject: touch.subject || "", body: touch.body || "" };
      const written = applyTouchBlock(before, block, touch);
      const saved = await saveTouch({ ...touch, ...written });
      if (saved) keepSparkleDraft("outreach_copy", "outreach_touch", touch.id, null, written, before);
      if (saved) setDraftedTouch((d) => ({ touchId: touch.id, n: (d?.n || 0) + 1 }));
    } catch (err) {
      setOutreachDraftError(err?.message || "I couldn't reach Brill just then. Nothing in this touch was touched.");
    } finally {
      setSparklingTouchId(null);
    }
  };

  /* A rewrite has no undo, so a touch with words asks first; an empty one does
     not — there is nothing to lose, and a confirm on every first draft is a
     click on every touch of every sequence. Same line Sites and Course draw. */
  const askTouchSparkle = (sequence, touch, touches) => {
    if (!sequence || !touch) return;
    const label = (roleDef(touch?.touchRole)?.label || "this touch").toLowerCase();
    if (touchWords(touch) === 0) { runTouchSparkle(sequence, touch, touches); return; }
    setConfirm({
      title: `Rewrite ${label}?`,
      message: `Brill writes ${label} again from your locked work. What's in it now is replaced, and there's no undo.`,
      confirmLabel: "Rewrite it",
      danger: true,
      onConfirm: async () => { setConfirm(null); await runTouchSparkle(sequence, touch, touches); },
    });
  };
  /* Which scope the drafting screen OPENS in — the whole sequence from "Write
     this sequence with Brill", one touch from a touch's chat icon. */
  const [draftingWhole, setDraftingWhole] = useState(false);

  /* Sequence copy always has a sequence open. Falls back to the first whenever
     the selection is empty or points at one that's since been deleted — the same
     rule `copyPage` and `copyModule` follow. */
  const copySequence = outreachView !== "copy" ? null
    : (sequences.find((s) => s.id === openSequenceId) || sequences[0] || null);

  /* A sequence created before this panel existed, or one whose second touch
     write failed, is short a touch and has no other way to get one back. Run in
     an effect rather than in the click handler because the touches may still be
     loading at that moment, and writing against a list that hasn't arrived would
     double them. `ensureTouches` is itself idempotent; this only decides when to
     ask. */
  const openSeqId = copySequence?.id || null;
  useEffect(() => {
    if (!openSeqId || outreachLoading) return;
    const seq = sequences.find((s) => s.id === openSeqId);
    /* Only the two ends are required — she may have removed every middle
       touch on purpose. */
    const roles = new Set(touchesFor(seq?.id).map((t) => t.touchRole));
    if (seq && (!roles.has("opening") || !roles.has("close"))) ensureTouches(seq);
  }, [openSeqId, outreachLoading, sequences, touchesFor, ensureTouches]);

  /* ONE editing screen for a sequence, always with the sequence list beside it —
     opening a row from the table lands in exactly the same place as switching to
     Sequence copy. The website learned this the hard way (commit 228b5ac). */
  const openSequence = (s) => { setOpenSequenceId(s.id); setDraftingTouchId(null); setOutreachView("copy"); };

  /* A new sequence is blank, so there is nothing to see on the table and
     everything to write — it opens itself. */
  const addAndOpenSequence = async (type) => {
    const saved = await addSequence(type);
    if (saved && saved.id) openSequence(saved);
  };

  /* Done is her mark on the sequence — from the rail's dot, the same as a page
     or a module. */
  const markSequence = useCallback(
    (sequence, status) => saveSequence({ ...sequence, status }),
    [saveSequence],
  );

  /* Clearing a touch empties its words — the touch itself stays, because a
     sequence is always three. An empty one clears without asking; one with
     words asks, the same rule as a course block. */
  /* Removing a middle touch deletes it, words and all. An empty one goes
     without asking. */
  const askRemoveTouch = (t) => {
    if (!t) return;
    if (touchWords(t) === 0) { removeTouch(t); return; }
    setConfirm({
      title: `Remove touch ${t.position || ""}?`.replace("  ", " "),
      message: `The ${roleDef(t.touchRole).label.toLowerCase()} and everything written in it are deleted. The other touches move up. There's no undo.`,
      confirmLabel: "Remove it",
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        const ok = await removeTouch(t);
        if (ok && draftingTouchId === t.id) setDraftingTouchId(null);
      },
    });
  };
  const askClearTouch = (t) => {
    if (!t) return;
    const cleared = { ...t, subject: "", body: "" };
    if (touchWords(t) === 0) { saveTouch(cleared); return; }
    setConfirm({
      title: `Clear ${roleDef(t.touchRole).label.toLowerCase()}?`,
      message: "Everything written in this touch is deleted. The touch and its channel stay. There's no undo.",
      confirmLabel: "Clear it",
      danger: true,
      onConfirm: async () => { setConfirm(null); await saveTouch(cleared); },
    });
  };

  const askDeleteSequence = (s) => setConfirm({
    title: "Delete this sequence?",
    message: `"${sequenceLabel(s)}" and all three of its touches will be removed. This can't be undone.`,
    confirmLabel: "Delete",
    danger: true,
    onConfirm: async () => {
      const ok = await deleteSequence(s.id);
      setConfirm(null);
      // Deleting the sequence that's open has to clear the selection, or she's
      // left editing a row that no longer exists and a keystroke would write it
      // back. Clearing it lets Sequence copy fall back to her first one.
      if (ok && openSequenceId === s.id) setOpenSequenceId(null);
    },
  });

  /* Her CALL SCRIPTS (Grow), and the sections inside each — Outreach's
     shape, see momentum/callScripts.jsx. Both tables loaded whole, for the same
     reason: the table shows words and a dot per section on every row. */
  const {
    scripts: callScripts, loading: scriptsLoading, error: rawScriptsError, clearError: clearScriptsError,
    sectionsFor: scriptSectionsFor, saveScript, addScript, deleteScript, reorderScripts,
    saveSection: saveScriptSection, addSection: addScriptSection, removeSection: removeScriptSection,
    reorderSections: reorderScriptSections,
  } = useCallScripts();
  /* Until supabase/0020 runs, the tables do not exist and every read says so
     in Postgres words. Said in words she can act on. */
  const scriptsError = rawScriptsError && /call_script/i.test(rawScriptsError)
    ? "Scripts need a database update first: supabase/0020_call_scripts.sql."
    : rawScriptsError;
  const [scriptsView, setScriptsView] = useState("table");
  const [openScriptId, setOpenScriptId] = useState(null);
  const [draftingScriptSectionId, setDraftingScriptSectionId] = useState(null);
  /* THE ONE-PRESS WRITE ON A SLIDE. The fifth row to get it, which is all of
     them: the row itself has always been shared (momentum/block.jsx), but each
     panel has to hand it a `sparkle`, and three of them never did. See
     sparkleEverywhere.test.js — it fails if a panel stops. */
  const [sparklingSlideId, setSparklingSlideId] = useState(null);
  /* WHICH SLIDE SHE PICKED IN THE RAIL. The count is what lets her pick the
     same one twice — press it, close the row, press it again — because the
     row only opens when something about it changes. */
  const [pickedSlide, setPickedSlide] = useState(null);
  const pickedSlideId = pickedSlide?.id || null;

  /* Scrolls the picked slide into view. The rail can be taller than the deck
     is on screen, so opening a row halfway down does nothing visible without
     it. Runs after the row has rendered, hence the effect rather than the
     click handler. */
  useEffect(() => {
    if (!pickedSlide?.id) return;
    const el = document.getElementById(`slide-${pickedSlide.id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [pickedSlide]);
  const [draftedSlide, setDraftedSlide] = useState(null);

  const runSlideSparkle = async (deck, section, slide, sections, slides) => {
    setSparklingSlideId(slide.id);
    setSlideDraftError(null);
    try {
      const token = await session?.getToken();
      const reply = await deckDraftReply({
        token, chatContext: momentumChatContext,
        context: buildSlideContext({ presentation: deck, section, slide, sections, slides, offers }),
        messages: [{
          role: "user",
          content: `Draft this ${slideKind(slide?.slideType).label.toLowerCase()} slide and its speaker notes from what I've locked, and put it straight in the fields. No questions first — draft it, and I'll tell you what to change.`,
        }],
      });
      const block = parseSlideBlock(reply, slide);
      if (!block || !Object.keys(block).length) {
        setSlideDraftError(`Brill didn't have enough to draft that slide — open the chat on it and say what it has to land.`);
        return;
      }
      const written = applySlideBlock(slide.content || {}, block, slide);
      const saved = await saveSlide({ ...slide, content: written });
      if (saved) keepSparkleDraft("presentation_copy", "presentation_slide", slide.id, null, written, slide.content);
      if (saved) setDraftedSlide((d) => ({ slideId: slide.id, n: (d?.n || 0) + 1 }));
    } catch (err) {
      setSlideDraftError(err?.message || "I couldn't reach Brill just then. Nothing on this slide was touched.");
    } finally {
      setSparklingSlideId(null);
    }
  };

  const askSlideSparkle = (deck, section, slide, sections, slides) => {
    if (!deck || !slide) return;
    const label = slideKind(slide?.slideType).label.toLowerCase();
    if (slideWords(slide) === 0) { runSlideSparkle(deck, section, slide, sections, slides); return; }
    setConfirm({
      title: `Rewrite this ${label} slide?`,
      message: `Brill writes the slide and its notes again from your locked work. What's on it now is replaced, and there's no undo.`,
      confirmLabel: "Rewrite it",
      danger: true,
      onConfirm: async () => { setConfirm(null); await runSlideSparkle(deck, section, slide, sections, slides); },
    });
  };

  /* THE ONE-PRESS WRITE ON A SCRIPT SECTION (her ask, 2026-09-17: "call scripts
     also need a sparkle"). The fourth panel to get it, and the same three
     pieces every time: who is busy, the confirm before a rewrite, and the stamp
     that remounts the row so the words appear without opening the chat. */
  const [slideDraftError, setSlideDraftError] = useState(null);
  const [sparklingScriptSectionId, setSparklingScriptSectionId] = useState(null);
  const [draftedScriptSection, setDraftedScriptSection] = useState(null);
  const [scriptDraftError, setScriptDraftError] = useState(null);

  const runScriptSparkle = async (script, section, sections) => {
    setSparklingScriptSectionId(section.id);
    setScriptDraftError(null);
    try {
      const token = await session?.getToken();
      const reply = await scriptDraftReply({
        token, script, section, sections, offers, chatContext: momentumChatContext,
        messages: [{
          role: "user",
          content: `Draft the ${sectionName(section).toLowerCase()} of this call from the goal and what I've locked, and put it straight in the fields. No questions first — draft it, and I'll tell you what to change.`,
        }],
      });
      const block = parseScriptSectionBlock(reply, section);
      if (!block || !Object.keys(block).length) {
        setScriptDraftError(`Brill didn't have enough to draft the ${sectionName(section).toLowerCase()} — open the chat on it and say who's on this call.`);
        return;
      }
      const written = applyScriptSectionBlock(section.content || {}, block, section);
      const saved = await saveScriptSection({ ...section, content: written });
      if (saved) keepSparkleDraft(isVideo(script) ? "video_script_copy" : "call_script_copy", "call_script_section", section.id, null, written, section.content);
      if (saved) setDraftedScriptSection((d) => ({ sectionId: section.id, n: (d?.n || 0) + 1 }));
    } catch (err) {
      setScriptDraftError(err?.message || "I couldn't reach Brill just then. Nothing in this section was touched.");
    } finally {
      setSparklingScriptSectionId(null);
    }
  };

  const askScriptSparkle = (script, section, sections) => {
    if (!script || !section) return;
    const label = sectionName(section).toLowerCase();
    if (sectionWords(section) === 0) { runScriptSparkle(script, section, sections); return; }
    setConfirm({
      title: `Rewrite the ${label}?`,
      message: `Brill writes the ${label} again from this call's goal and your locked work. What's in it now is replaced, and there's no undo.`,
      confirmLabel: "Rewrite it",
      danger: true,
      onConfirm: async () => { setConfirm(null); await runScriptSparkle(script, section, sections); },
    });
  };
  const [draftingWholeScript, setDraftingWholeScript] = useState(false);
  /* Call copy always has a script open — the first in ladder order when the
     selection is empty or gone. The same rule `copySequence` follows. */
  const copyScript = scriptsView !== "copy" ? null
    : (callScripts.find((s) => s.id === openScriptId)
      || groupScripts(callScripts).flatMap((g) => g.rows)[0] || null);
  const openScript = (s) => { setOpenScriptId(s.id); setDraftingScriptSectionId(null); setScriptsView("copy"); };
  /* `opts` carries the medium and, for a video, its form — the shell's Add menu
     asks call-or-video first (2026-09-17). */
  const addAndOpenScript = async (type, presetId, opts) => {
    const saved = await addScript(type, presetId, opts);
    if (saved && saved.id) openScript(saved);
  };
  const markScript = useCallback((script, status) => saveScript({ ...script, status }), [saveScript]);
  /* Removing a section deletes it, words and all. An empty one goes without
     asking — the same rule as a touch. */
  const askRemoveScriptSection = (s) => {
    if (!s) return;
    const drop = async () => {
      const ok = await removeScriptSection(s.id);
      if (ok && draftingScriptSectionId === s.id) setDraftingScriptSectionId(null);
    };
    if (sectionWords(s) === 0) { drop(); return; }
    setConfirm({
      title: `Remove ${sectionName(s).toLowerCase()}?`,
      message: "This section and everything written in it are deleted. The other sections move up. There's no undo.",
      confirmLabel: "Remove it",
      danger: true,
      onConfirm: async () => { setConfirm(null); await drop(); },
    });
  };
  /* Her NEWSLETTERS (Grow) — one source piece, three types, and the sections
     inside each (momentum/newsletter.jsx). Scripts' shape. Both tables loaded
     whole, for the same reason as the others: the table shows words and a dot
     per section on every row. */
  const {
    newsletters, loading: newslettersLoading, error: rawNewslettersError, clearError: clearNewslettersError,
    blocksLoading: newsletterBlocksLoading, blocksFor: newsletterBlocksFor, saveNewsletter, addNewsletter, makeVariant, deleteNewsletter,
    reorderNewsletters, saveBlock: saveNewsletterBlock, addBlock: addNewsletterBlock,
    removeBlock: removeNewsletterBlock, reorderBlocks: reorderNewsletterBlocks,
  } = useNewsletters();
  /* Until supabase/0025 runs the tables do not exist, and every read says so in
     Postgres words. Said in words she can act on — the same wrapper Scripts and
     Presentations carry. */
  const newslettersError = rawNewslettersError && /newsletter/i.test(rawNewslettersError)
    ? "Newsletters need a database update first: supabase/0025_newsletters.sql."
    : rawNewslettersError;
  const [newslettersView, setNewslettersView] = useState("table");
  const [openNewsletterId, setOpenNewsletterId] = useState(null);
  const [draftingNewsletterBlockId, setDraftingNewsletterBlockId] = useState(null);
  const [draftingWholeNewsletter, setDraftingWholeNewsletter] = useState(false);
  const [sparklingNewsletterBlockId, setSparklingNewsletterBlockId] = useState(null);
  const [draftedNewsletterBlock, setDraftedNewsletterBlock] = useState(null);
  const [newsletterDraftError, setNewsletterDraftError] = useState(null);
  const [makingVariant, setMakingVariant] = useState(null);

  /* HER PILLARS, read from her locked visibility work — the same parser the
     Social Generator reads them with (utils/pillars.js), never a second list. */
  const newsletterPillars = useMemo(
    () => pillarLabels(acceleratorOutputs?.social?.["Content Pillars"]),
    [acceleratorOutputs],
  );

  /* THE DESIGNED EMAIL'S BRAND (2026-10-12): her logo, name and colours from
     Me → Brand, her locked palette for any colour Me leaves empty — through
     `brandSkin`, the ONE reader the decks and the site brief use too. */
  const emailBrand = useMemo(() => {
    return {
      logoUrl: userSettings?.logoUrl || "",
      name: userSettings?.businessName || "",
      skin: brandSkin({ brand: userSettings?.brand, voiceOutputs: acceleratorOutputs?.voice || {} }).skin,
      /* The name she signs with (Me → Business), then the one Brill calls her. */
      signer: userSettings?.signoff || userSettings?.displayName || "",
    };
  }, [acceleratorOutputs, userSettings?.logoUrl, userSettings?.businessName, userSettings?.brand, userSettings?.signoff, userSettings?.displayName]);

  /* Newsletter copy always has a piece open — the first in band order when the
     selection is empty or gone. The same rule `copyScript` follows. */
  const copyNewsletter = newslettersView !== "copy" ? null
    : (newsletters.find((n) => n.id === openNewsletterId)
      || groupNewsletters(newsletters).flatMap((g) => g.rows)[0] || null);
  const openNewsletter = (n) => {
    setOpenNewsletterId(n.id);
    setDraftingNewsletterBlockId(null);
    setNewslettersView("copy");
  };
  const addAndOpenNewsletter = async (type) => {
    const saved = await addNewsletter(type);
    if (saved && saved.id) openNewsletter(saved);
  };
  /* MAKE A CUT: the variant is created empty, with its own sections, and she
     lands in it — Brill writes the cut from the source in the chat, because a
     cut is "not just a trim" (her words). */
  const makeAndOpenVariant = async (source, type) => {
    setMakingVariant(type);
    try {
      const saved = await makeVariant(source, type);
      if (saved && saved.id) openNewsletter(saved);
    } finally {
      setMakingVariant(null);
    }
  };
  const markNewsletter = useCallback((n, status) => saveNewsletter({ ...n, status }), [saveNewsletter]);
  /* Removing a section deletes it, words and all. An empty one goes without
     asking — the same rule as a script's. */
  const askRemoveNewsletterBlock = (b) => {
    if (!b) return;
    const drop = async () => {
      const ok = await removeNewsletterBlock(b.id);
      if (ok && draftingNewsletterBlockId === b.id) setDraftingNewsletterBlockId(null);
    };
    if (nlBlockWords(b) === 0) { drop(); return; }
    setConfirm({
      title: `Remove ${nlBlockName(b).toLowerCase()}?`,
      message: "This section and everything written in it are deleted. The other sections move up. There's no undo.",
      confirmLabel: "Remove it",
      danger: true,
      onConfirm: async () => { setConfirm(null); await drop(); },
    });
  };
  const askDeleteNewsletter = (n) => {
    if (!n) return;
    const cuts = newsletters.filter((x) => x.sourceId === n.id).length;
    setConfirm({
      title: `Delete ${newsletterLabel(n)}?`,
      /* The cuts SURVIVE — 0025 sets their source_id null rather than cascading,
         because a cut she has edited is her work. Said here, or deleting a post
         reads as deleting three. */
      message: cuts
        ? `This piece and everything written in it are deleted. The ${cuts === 1 ? "cut" : `${cuts} cuts`} made from it stay, and lose the link back. There's no undo.`
        : "This piece and everything written in it are deleted. There's no undo.",
      confirmLabel: "Delete it",
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        const ok = await deleteNewsletter(n.id);
        if (ok && openNewsletterId === n.id) setOpenNewsletterId(null);
      },
    });
  };

  /* THE ONE-PRESS WRITE on a newsletter section — the same shape as every other
     panel's (sparkleEverywhere.test.js pins all of them). A CUT carries the
     source it was cut from, or Brill has nothing to cut from. */
  const newsletterSourceOf = (n) => (isSource(n) ? null : newsletters.find((x) => x.id === n?.sourceId) || null);

  const runNewsletterSparkle = async (newsletter, block, blocks) => {
    setSparklingNewsletterBlockId(block.id);
    setNewsletterDraftError(null);
    try {
      const token = await session?.getToken();
      const source = newsletterSourceOf(newsletter);
      const reply = await newsletterDraftReply({
        token, newsletter, block, blocks, offers, source,
        sourceBlocks: source ? newsletterBlocksFor(source.id) : [],
        pillars: newsletterPillars, chatContext: momentumChatContext,
        messages: [{
          role: "user",
          content: isSource(newsletter)
            ? `Draft ${nlBlockName(block).toLowerCase()} of this post from my pillar and what I've locked, and put it straight in the fields. No questions first — draft it, and I'll tell you what to change.`
            : `Cut ${nlBlockName(block).toLowerCase()} from the source post — one thread of it, opening differently — and put it straight in the fields. No questions first.`,
        }],
      });
      const parsed = parseNewsletterBlockReply(reply, block);
      if (!parsed || !Object.keys(parsed).length) {
        setNewsletterDraftError(`Brill didn't have enough to draft ${nlBlockName(block).toLowerCase()} — open the chat on it and say what this piece is about.`);
        return;
      }
      const written = applyNewsletterBlockReply(block.content || {}, parsed, block);
      const saved = await saveNewsletterBlock({ ...block, content: written });
      if (saved) keepSparkleDraft("newsletter_copy", "newsletter_block", block.id, null, written, block.content);
      if (saved) setDraftedNewsletterBlock((d) => ({ blockId: block.id, n: (d?.n || 0) + 1 }));
    } catch (err) {
      setNewsletterDraftError(err?.message || "I couldn't reach Brill just then. Nothing in this section was touched.");
    } finally {
      setSparklingNewsletterBlockId(null);
    }
  };

  const askNewsletterSparkle = (newsletter, block, blocks) => {
    if (!newsletter || !block) return;
    const label = nlBlockName(block).toLowerCase();
    if (nlBlockWords(block) === 0) { runNewsletterSparkle(newsletter, block, blocks); return; }
    setConfirm({
      title: `Rewrite ${label}?`,
      message: `Brill writes ${label} again from this piece and your locked work. What's in it now is replaced, and there's no undo.`,
      confirmLabel: "Rewrite it",
      danger: true,
      onConfirm: async () => { setConfirm(null); await runNewsletterSparkle(newsletter, block, blocks); },
    });
  };

  /* Her PRESENTATIONS (Build) — a deck, its sections, its slides. Admin-only
     while it is tested (toolStatus). Three tables loaded whole, for the same
     reason as the others. */
  const {
    presentations, loading: decksLoading, error: rawDecksError, clearError: clearDecksError,
    sectionsFor: deckSectionsFor, slidesFor: deckSlidesFor,
    savePresentation, addPresentation, deletePresentation, reorderPresentations,
    saveSection: saveDeckSection, addSection: addDeckSection, deleteSection: deleteDeckSection,
    saveSlide, addSlides, removeSlide, reorderSlides,
  } = usePresentations();
  const decksError = rawDecksError && /presentation/i.test(rawDecksError)
    ? "Presentations need a database update first: supabase/0021_presentations.sql."
    : rawDecksError;
  const [decksView, setDecksView] = useState("table");
  const [openDeckId, setOpenDeckId] = useState(null);
  /* Which slide she is writing with Brill, and in which scope. */
  const [draftingSlide, setDraftingSlide] = useState(null);   // { sectionId, slideId, whole }
  /* Brill's suggested slides, per section, waiting on her word (rule 4). */
  const [proposals, setProposals] = useState({});
  const copyDeck = decksView !== "copy" ? null
    : (presentations.find((p) => p.id === openDeckId)
      || [...presentations].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))[0] || null);
  const openDeck = (p) => { setOpenDeckId(p.id); setDraftingSlide(null); setDecksView("copy"); };
  const addAndOpenDeck = async (type) => {
    const saved = await addPresentation(type);
    if (saved && saved.id) openDeck(saved);
  };
  const markDeck = useCallback((deck, status) => savePresentation({ ...deck, status }), [savePresentation]);
  const runProposal = async (deck, section) => {
    setProposals((p) => ({ ...p, [section.id]: { ...(p[section.id] || {}), busy: true, error: null } }));
    try {
      const token = await session?.getToken();
      const out = await proposeSlides({
        token, deck, section, offers, chatContext: momentumChatContext,
        sections: deckSectionsFor(deck.id), slides: deckSlidesFor(deck.id),
      });
      setProposals((p) => ({ ...p, [section.id]: { busy: false, error: null, ...out } }));
    } catch (err) {
      setProposals((p) => ({ ...p, [section.id]: { ...(p[section.id] || {}), busy: false, error: err?.message || "I couldn't reach Brill just then. Nothing in this presentation was touched." } }));
    }
  };
  const acceptProposal = async (deck, section) => {
    const prop = proposals[section.id];
    if (!prop?.slides?.length) return;
    const saved = await addSlides(deck, section, prop.slides);
    if (saved.length) setProposals((p) => { const n = { ...p }; delete n[section.id]; return n; });
  };
  const dismissProposal = (section) => setProposals((p) => { const n = { ...p }; delete n[section.id]; return n; });
  const askRemoveSlide = (s) => {
    if (!s) return;
    const drop = async () => {
      const ok = await removeSlide(s.id);
      if (ok && draftingSlide?.slideId === s.id) setDraftingSlide(null);
    };
    if (slideWords(s) === 0) { drop(); return; }
    setConfirm({
      title: `Remove this ${slideKind(s.slideType).label.toLowerCase()} slide?`,
      message: "The slide and its speaker notes are deleted. There's no undo.",
      confirmLabel: "Remove it", danger: true,
      onConfirm: async () => { setConfirm(null); await drop(); },
    });
  };
  const askDeleteDeckSection = (section, sectionSlides = []) => {
    const run = async () => { await deleteDeckSection(section); if (draftingSlide?.sectionId === section.id) setDraftingSlide(null); };
    if (!sectionSlides.length) { run(); return; }
    setConfirm({
      title: `Delete "${section.name || "this section"}"?`,
      message: `The section and its ${sectionSlides.length} slide${sectionSlides.length === 1 ? "" : "s"} are deleted. There's no undo.`,
      confirmLabel: "Delete", danger: true,
      onConfirm: async () => { setConfirm(null); await run(); },
    });
  };
  const askDeleteDeck = (p) => setConfirm({
    title: "Delete this presentation?",
    message: `"${presentationLabel(p)}" and every slide in it will be removed. This can't be undone.`,
    confirmLabel: "Delete", danger: true,
    onConfirm: async () => {
      const ok = await deletePresentation(p.id);
      setConfirm(null);
      if (ok && openDeckId === p.id) setOpenDeckId(null);
    },
  });

  const askDeleteScript = (s) => setConfirm({
    title: "Delete this call script?",
    message: `"${scriptLabel(s)}" and every section in it will be removed. This can't be undone.`,
    confirmLabel: "Delete",
    danger: true,
    onConfirm: async () => {
      const ok = await deleteScript(s.id);
      setConfirm(null);
      if (ok && openScriptId === s.id) setOpenScriptId(null);
    },
  });

  /* Table vs Module copy, and which module is open in it. Local to the panel
     rather than a route, for the same reason Website's is: it's a way of looking
     at the same thing, and a Back button that landed her in the other view would
     be wrong. */
  const [courseView, setCourseView] = useState("table");
  const [openModuleId, setOpenModuleId] = useState(null);
  /* Module copy always has a module open. This falls back to the first whenever
     the selection is empty or points at one that's since been deleted — the
     same rule `copyPage` follows below. */
  const copyModuleIndex = courseView !== "copy" ? -1
    : Math.max(0, courseModules.findIndex((m) => m.id === openModuleId));
  const copyModule = courseView !== "copy" ? null : (courseModules[copyModuleIndex] || null);

  /* ONE editing screen for a module, always with the module list beside it —
     opening a row from the table lands in exactly the same place as switching to
     Module copy. The website learned this the hard way (commit 228b5ac). */
  const openCourseModule = (m) => { setOpenModuleId(m.id); setCourseView("copy"); };

  /* A new module is blank, so there is nothing to see on the table and
     everything to write — it opens itself. */
  const addAndOpenModule = async () => {
    const saved = await addModule();
    if (saved && saved.id) openCourseModule(saved);
  };

  const { session } = useSession();
  /* PUBLISH TO HER BLOG (2026-11-03, admin only). The server marks the post
     published; the copy here is brought level at once, because every save of
     a piece writes ALL its fields — the next keystroke would otherwise write
     the old address and "never published" straight back over it. */
  const publishBlogPost = useCallback(async (n) => {
    const r = await publishToBlog({ token: await session?.getToken().catch(() => null), newsletterId: n.id });
    if (r.ok) await saveNewsletter({ ...n, sentAt: r.sentAt, canonicalUrl: r.canonicalUrl, urlSlug: r.urlSlug });
    return r;
  }, [session, saveNewsletter]);
  /* RECORDING THE VOICE (supabase/0030) for the six one-press sparkles: what
     Brill wrote and what the block held before, once the save has landed.
     Fired, never awaited — a sparkle that saved her words is done. */
  const recordVoice = useVoiceRecorder();
  const keepSparkleDraft = (tool, recordType, recordId, blockId, draft, replaced) =>
    recordVoice({ tool, recordType, recordId, blockId, how: "sparkle", draft, replaced });
  /* Her locked work plus who she is, as every drafting surface in here needs it.
     Named once rather than rebuilt inline per call site — three copies of the
     same four keys is how one of them quietly loses `userSettings` and an
     archetype stops reaching Brill. */
  const momentumChatContext = { acceleratorOutputs, activeNiche, outputStamps, userSettings };

  /* WRITE FIRST (2026-11-03): the writer's sections that are not her words —
     the short answer, the claims, the FAQ, the ask — are made the first time
     the Brill menu fills one, and updated after that. */
  const ensureNewsletterBlock = useCallback(async (newsletterId, kind, content) => {
    const have = newsletterBlocksFor(newsletterId).find((b) => b.blockType === kind);
    if (have) return saveNewsletterBlock({ ...have, content: { ...(have.content || {}), ...content } });
    const made = await addNewsletterBlock(newsletterId, kind);
    return made ? saveNewsletterBlock({ ...made, content: { ...(made.content || {}), ...content } }) : null;
  }, [newsletterBlocksFor, saveNewsletterBlock, addNewsletterBlock]);
  const askNewsletterShape = async (newsletter, { mode, writing, offer, ask }) => newsletterShapeReply({
    token: await session?.getToken().catch(() => null), chatContext: momentumChatContext,
    newsletter, writing, offer, pillars: newsletterPillars, mode, ask,
  });
  const previewNewsletter = async (n) => previewBlogPost({
    token: await session?.getToken().catch(() => null), newsletterId: n.id,
  });

  /* WRITING A BLOCK WITH BRILL — two doors, and they are different things.

     The SPARKLE is one click: no conversation, Brill drafts this block from the
     module's outcome and it lands in the fields. The CHAT is the brainstorm —
     the drafting screen the website's sections have, chat on the left, the
     fields it writes into on the right.

     Both cost one call to Brill. The sparkle is the cheaper of the two per
     result, because it has no conversation riding along on every message.       */
  /* What the last pull did. A button that correctly adds nothing looks exactly
     like a broken one, so it reports either way. */
  const [courseNotice, setCourseNotice] = useState(null);
  const doPullCurriculum = async () => {
    setCourseNotice(null);
    if (missingModules === 0) {
      setCourseNotice("Every module in your curriculum is already here — nothing to bring across.");
      return;
    }
    const made = await pullCurriculum();
    setCourseNotice(made.length
      ? `Brought across ${made.length} module${made.length === 1 ? "" : "s"} from your curriculum.`
      : "Nothing came across — your curriculum in My Offer may have changed shape since it was locked.");
  };

  const [draftingBlockId, setDraftingBlockId] = useState(null);
  /* The whole-module scope: which one the drafting screen opens in. The
     module chat is the Drafter's record scope now, not a screen of its own. */
  const [draftingWholeModule, setDraftingWholeModule] = useState(false);
  const [sparkleId, setSparkleId] = useState(null);
  const [draftError, setDraftError] = useState(null);
  /* Which block Brill just wrote, so it can remount with the new words in it and
     open itself. A block holds a DRAFT of its own content, so without the
     remount the row would save and the fields would still show the old copy. */
  const [drafted, setDrafted] = useState(null);

  const runSparkle = async (m, kindId) => {
    const kind = blockKind(kindId);
    setSparkleId(kindId);
    setDraftError(null);
    try {
      const token = await session?.getToken();
      const reply = await courseDraftReply({
        token, module: m, kindId, chatContext: momentumChatContext,
        /* The sparkle is the one-press draft, and it was the one path the
           course offer did not reach on 2026-09-24 — the chat had it, this did
           not. Her report the same day: an Archetype Assessment course drafted
           about the flagship. Same answer the drafter gets. */
        offer: offers.find((o) => o.id === course?.offerId) || null,
        messages: [{
          role: "user",
          content: `Draft the ${kind?.label?.toLowerCase() || "block"} for this module from its outcome, and put it straight in the fields. No questions first — draft it, and I'll tell you what to change.`,
        }],
      });
      const block = parseCourseBlock(reply, kindId);
      if (!block || !Object.keys(block).length) {
        /* A reply with no block is Brill asking a question rather than drafting.
           Saying so is more useful than a spinner that stops with nothing
           different on screen. */
        setDraftError(`Brill didn't have enough to draft the ${kind?.label?.toLowerCase() || "block"} — open the chat on it and tell her the angle.`);
        return;
      }
      const merged = applyCourseBlock(blockContent(m, kindId), block, kindId);
      const saved = await saveModule(withBlockContent(m, kindId, merged));
      if (saved) keepSparkleDraft("course_copy", "course_module", m.id, kindId, merged, blockContent(m, kindId));
      if (saved) setDrafted((d) => ({ moduleId: m.id, kindId, n: (d?.n || 0) + 1 }));
    } catch (err) {
      setDraftError(err?.message || "I couldn't reach Brill just then. Nothing in this module was touched.");
    } finally {
      setSparkleId(null);
    }
  };

  /* A rewrite has no undo, so a block with words in it asks first. An empty one
     doesn't — there is nothing to lose and a confirm on every first draft would
     be a click she has to make twelve times. */
  const askSparkle = (m, kindId) => {
    if (!m) return;
    if (blockWords(m, kindId) === 0) { runSparkle(m, kindId); return; }
    const label = blockKind(kindId)?.label || "this block";
    setConfirm({
      title: `Rewrite ${label.toLowerCase()}?`,
      message: `Brill writes ${label.toLowerCase()} again from this module's outcome. What's in it now is replaced, and there's no undo.`,
      confirmLabel: "Rewrite it",
      danger: true,
      onConfirm: async () => { setConfirm(null); await runSparkle(m, kindId); },
    });
  };

  /* Emptying a block is destructive and there is no undo, so it asks — the same
     rule the Website section bin has always followed, and the one this one was
     missing (spec 5.2). The bin clears the words AND drops the block from the
     module in one write, so "remove" here means "the teaching outline you wrote
     is gone", not "this row is tidied away".

     An EMPTY block skips the question: there is nothing to lose, and a confirm
     on a block she just added by mistake is a click that protects nothing. Same
     reasoning as askSparkle above. */
  const askClearBlock = (m, kindId, cleared) => {
    if (!m) return;
    if (blockWords(m, kindId) === 0) { removeBlock(m, kindId, cleared); return; }
    const label = blockKind(kindId)?.label || "this block";
    setConfirm({
      title: `Clear ${label.toLowerCase()}?`,
      message: `Everything written in ${label.toLowerCase()} is deleted and the block comes off this module. There's no undo.`,
      confirmLabel: "Clear it",
      danger: true,
      onConfirm: async () => { setConfirm(null); await removeBlock(m, kindId, cleared); },
    });
  };

  /* Table vs Page copy. Local to the panel rather than a route, because it's a
     way of looking at the same thing — a back button that landed her in the
     other view would be wrong. */
  const [websiteView, setWebsiteView] = useState("table");
  const [editingPage, setEditingPage] = useState(null);
  const [savingPage, setSavingPage] = useState(false);
  /* Which section she's writing with Brill. Lives up here rather than inside the
     page editor because the PAGE LIST has to know: writing one section is a
     two-column screen of its own, and a rail of other pages beside it is a third
     column of things she isn't doing. */
  const [draftingSectionId, setDraftingSectionId] = useState(null);
  /* …and whether that screen is on the whole page. */
  const [draftingWholePage, setDraftingWholePage] = useState(false);

  /* A page's copy blocks. Loaded for every page at once rather than per page —
     the Website table shows a block count on each row, so a per-page fetch would
     be a request inside a render loop. See utils/websiteSections.js. */
  const {
    loading: sectionsLoading, error: sectionsError, clearError: clearSectionsError,
    sectionsFor, saveSection, deleteSection, ensureSections, refreshFromSource,
    addSection, changeKind, moveSection, reorderSections,
  } = useWebsiteSections();

  /* The one-time split of locked copy into real blocks, run when she opens a
     page. In an effect rather than in the click handler because sections may
     still be loading at that moment, and splitting against a list that hasn't
     arrived would double every block — this re-runs once loading settles.
     `ensureSections` is itself idempotent; this only decides when to ask. */
  const openPageId = editingPage && !editingPage.isNew ? editingPage.id : null;
  useEffect(() => {
    if (!openPageId || sectionsLoading) return;
    /* The SAVED page, not the open draft. Two reasons: the draft is a new object
       on every keystroke, so depending on it would run this on each one; and the
       split must read the page as stored, not as half-retyped. */
    const page = pages.find((p) => p.id === openPageId);
    if (page) ensureSections(page, websiteOutputs);
  }, [openPageId, sectionsLoading, pages, ensureSections, websiteOutputs]);

  /* Switching page from inside the drafting chat keeps her in the chat, landing
     on the new page's banner. No waiting involved: `useWebsiteSections` loads
     every row for the account in one go (the Website table needs a block count
     per row), so another page's blocks are already in hand.

     Unless that page has never been opened — its locked copy is split into
     blocks on first open, so there is genuinely nothing to write yet. She lands
     on the page instead, the split runs, and she picks a block. */
  const switchPageInChat = (p) => {
    const start = startSection(sectionsFor(p.id));
    setEditingPage({ ...p });
    setDraftingSectionId(start ? start.id : null);
  };

  const askRefreshSections = (page) => setConfirm({
    title: "Replace this page's copy?",
    message: "My Website has been rewritten since these blocks were made. This replaces every block that came from it — anything you've edited in them will be lost. Blocks you added yourself are kept.",
    confirmLabel: "Replace",
    danger: true,
    onConfirm: async () => { await refreshFromSource(page, websiteOutputs); setConfirm(null); },
  });

  const askDeleteSection = (s) => setConfirm({
    title: "Delete this block?",
    message: `"${sectionHeading(s)}" will be removed from this page. This can't be undone.`,
    confirmLabel: "Delete",
    danger: true,
    onConfirm: async () => { await deleteSection(s.id); setConfirm(null); },
  });

  const askDeleteModule = (m) => setConfirm({
    title: "Delete this module?",
    message: `"${m.title || "Untitled module"}" and everything in it will be removed from your course. This can't be undone. The curriculum in My Offer isn't touched — you can pull it again.`,
    confirmLabel: "Delete",
    danger: true,
    onConfirm: async () => {
      const ok = await deleteModule(m.id);
      setConfirm(null);
      // Deleting the module that's open has to close it, or she's left editing a
      // row that no longer exists and a keystroke would write it back. The
      // drafting screen closes with it — it writes into that same row.
      if (ok && openModuleId === m.id) { setOpenModuleId(null); setDraftingBlockId(null); setDraftingWholeModule(false); }
    },
  });

  /* ONE editing screen for a page, always with the page list beside it. Opening
     a row from the table and switching to Page copy used to be two different
     screens — the same editor, one with a sidebar and one without, which made
     the sidebar look like it came and went. Both now land here. */
  const openPage = (p) => { setEditingPage({ ...p }); setDraftingSectionId(null); setWebsiteView("copy"); };

  /* Cancel throws away edits to the page's settings by reverting to the stored
     row. A page she never saved has no stored row to revert to, so that one
     drops back to the table. */

  const startAddPage = () => {
    /* Carries the site it is being added to. A page saved without one would
       belong to no site and show on no screen but the first — see the adoption
       note in websitePages.js. */
    openPage({ id: newPageId(), siteId: site?.id || null, title: "", slug: "", status: "draft", sortOrder: nextPageOrder, isNew: true });
  };

  /* A standard page from the Add menu saves immediately and opens — it already
     has its name, address and join, so there is nothing for her to fill in
     first. "Other" is the one that needs the form. */
  const addStandardAndOpen = async (slug) => {
    const saved = await addStandardPage(slug);
    if (saved) openPage(saved);
  };

  /* Page copy always has a page open. A brand-new one isn't in `pages` yet, so
     it holds its own; otherwise this falls back to the first page whenever the
     selection is empty or points at one that's since been deleted. */
  const copyPage = websiteView !== "copy" ? null
    : editingPage?.isNew ? editingPage
    : (pages.find((p) => p.id === editingPage?.id) || pages[0] || null);
  useEffect(() => {
    if (websiteView !== "copy" || !copyPage || copyPage.isNew) return;
    if (editingPage?.id !== copyPage.id) setEditingPage({ ...copyPage });
  }, [websiteView, copyPage, editingPage?.id]);

  /* `status` comes from WHICH save button she pressed — there is no Draft/Live
     control on the form. Publishing is what she means when she's finished, not a
     setting she comes back to tick. */
  /* A string is a status (the Done toggle); an object is a patch from a choice
     that saves as it is made — the offer picker. setDraft is not synchronous,
     so the new value has to travel with the call. */
  const savePage = async (change) => {
    if (!editingPage) return;
    const patch = typeof change === "string" ? { status: change } : (change || {});
    const { isNew, ...p } = { ...editingPage, ...patch };
    /* The address is normalised on the way out, not while she types — rewriting
       the field under the cursor makes it impossible to type "work-with-me"
       without fighting it. Blank falls back to the page name. */
    p.slug = slugify(p.slug) || slugify(p.title);
    /* Called on the way out of every field now, so most calls have nothing to
       write. A page has three settable things; comparing them is cheaper than a
       round trip, and without this, tabbing across the name and the address
       would write the same row twice. */
    const stored = pages.find((x) => x.id === p.id);
    if (!isNew && stored && stored.title === p.title && stored.slug === p.slug && stored.status === p.status
      && (stored.offerId || null) === (p.offerId || null)) return;
    setSavingPage(true);
    const saved = await persistPage(p);
    setSavingPage(false);
    /* Stays on the page she just saved rather than throwing her back to the
       list. Saving page settings is something she does mid-writing, and losing
       her place each time would make it feel like leaving the screen. Failure
       keeps the draft on screen with the banner. */
    if (saved) setEditingPage({ ...saved });
  };

  /* ── MARKING ONE DONE FROM THE RAIL ──────────────────────────────────────
     Her read, 2026-09-15: "you should also be able to click the yellow dot on
     the sidebar and have it shift to done." The dot was already what she looked
     at to know where a page stood; it just was not what she could press.

     These write the record STRAIGHT, not through `savePage`, which edits the
     one open in the editor — the whole point is marking a page she is NOT in.
     Both toggle, because Done is her mark and she has to be able to take it
     back without opening the record. */
  const markPage = useCallback(async (page, status) => {
    const saved = await persistPage({ ...page, status });
    /* AND THE PAGE ON SCREEN, if it is this one. Her report: "it needs to auto
       mark the draft button to done at the same time." The rail reads the STORE,
       which the line above updates, but the editor beside it renders
       `editingPage` — a local draft copy held here so a half-typed name is not
       a round trip per keystroke. Writing one and not the other leaves the dot
       teal and the pill four inches away still saying Draft, which is two
       answers to "is this page done" on one screen.

       Functional update, and guarded by id: she can mark a page in the rail that
       is NOT the one she has open, and this must not stamp that status onto
       whichever page happens to be in the editor. */
    if (saved) setEditingPage((d) => (d && d.id === page.id ? { ...d, status } : d));
    return saved;
  }, [persistPage]);
  /* A PAGE'S STYLE (supabase/0039, utils/siteStyles). Written STRAIGHT, like
     the dot above, from the STORED page — not the open draft, which may hold a
     half-typed name — and copied onto the draft only if the write landed. Until
     0039 has run there is no `style` column and this write fails; keeping the
     draft clean is what stops that failure riding along on every later save of
     the page (see the note on WEBSITE_PAGE_FIELDS). Returns true / false so
     the picker can say which. */
  const savePageStyle = useCallback(async (page, style) => {
    const stored = pages.find((p) => p.id === page?.id);
    if (!stored) return false;
    const saved = await persistPage({ ...stored, style: style || null });
    if (saved) setEditingPage((d) => (d && d.id === page.id ? { ...d, style: saved.style ?? null } : d));
    return !!saved;
  }, [persistPage, pages]);
  const markModule = useCallback(
    (module, status) => saveModule({ ...module, status }),
    [saveModule],
  );

  /* ── THE SPARKLE ON A WEBSITE SECTION ────────────────────────────────────
     One click, one call, no conversation: Brill drafts this section from her
     locked work and it lands in the fields, and she stays exactly where she is.
     The mirror of `askSparkle` on a course block, which has worked this way
     since it was built.

     IT USED TO BE A SECOND CHAT BUTTON. The sparkle and the speech bubble on a
     section row both called the same handler and both opened the drafting
     screen — so the one-click icon took her somewhere and wrote nothing, which
     is what she hit on 2026-09-15.

     WHY IT WRITES STRAIGHT IN, when rule 4 says Brill proposes and she locks:
     this is the exception the rule names (spec R2). It is her explicit click on
     THAT section, it never replaces words without asking first, and a one-click
     draft that then needs a second click is not one click. The CHAT is where a
     draft waits in a lock card, because there she is in a conversation and did
     not ask for anything to be written yet. */
  const [sparklingSectionId, setSparklingSectionId] = useState(null);
  const [sectionDraftError, setSectionDraftError] = useState(null);
  /* WHICH SECTION BRILL JUST WROTE, so its row can remount with the new words in
     it and open itself. A section block holds a DRAFT of its own content,
     initialised when it mounts — so saving the row updates Supabase, updates the
     word count in the rail, and leaves the row itself still reading "Nothing
     here yet". Going into the chat and back is what fixed it, because that
     remounts.

     Course has had this since it was built and said so in a comment two hundred
     lines up. Website's sparkle was wired the day after and did not follow it. */
  const [draftedSection, setDraftedSection] = useState(null);

  const runSectionSparkle = async (page, section) => {
    const kind = kindDef(section?.sectionType);
    setSparklingSectionId(section.id);
    setSectionDraftError(null);
    try {
      const token = await session?.getToken();
      const reply = await sectionDraftReply({
        token, page, kind, section, content: section.content || {},
        chatContext: momentumChatContext,
        extraScope: contextScopeFor(page, section),
        /* The sparkle sent NO offer until 2026-09-24 — only the chat did — so
           the one-press draft wrote from her Accelerator offer whatever the page
           was for. Same answer the chat gets now (utils/pageOffer). */
        offer: offerForPage(page, sites, offers),
        messages: [{
          role: "user",
          content: `Draft the ${kind?.label?.toLowerCase() || "section"} for this page from what I've locked, and put it straight in the fields. No questions first — draft it, and I'll tell you what to change.`,
        }],
      });
      const block = parseSectionBlock(reply, kind.id);
      if (!block || !Object.keys(block).length) {
        /* A reply with no block is Brill asking a question rather than drafting.
           Saying so beats a sparkle that stops with nothing different on screen. */
        setSectionDraftError(`Brill didn't have enough to draft the ${kind?.label?.toLowerCase() || "section"} — open the chat on it and tell her the angle.`);
        return;
      }
      const written = applySectionBlock(section.content || {}, block, kind.id);
      const saved = await saveSection({ ...section, content: written });
      if (saved) keepSparkleDraft("website_copy", "website_section", section.id, null, written, section.content);
      if (saved) setDraftedSection((d) => ({ sectionId: section.id, n: (d?.n || 0) + 1 }));
    } catch (err) {
      setSectionDraftError(err?.message || "I couldn't reach Brill just then. Nothing in this section was touched.");
    } finally {
      setSparklingSectionId(null);
    }
  };

  /* A rewrite has no undo, so a section with words in it asks first. An empty
     one doesn't — there is nothing to lose, and a confirm on every first draft
     is a click she would make on every section of every page. Same line the
     course sparkle draws. */
  const askSectionSparkle = (page, section) => {
    if (!page || !section) return;
    const kind = kindDef(section?.sectionType);
    if (!hasContent(section)) { runSectionSparkle(page, section); return; }
    setConfirm({
      title: `Rewrite ${(kind?.label || "this section").toLowerCase()}?`,
      message: `Brill writes ${(kind?.label || "this section").toLowerCase()} again from your locked work. What's in it now is replaced, and there's no undo.`,
      confirmLabel: "Rewrite it",
      danger: true,
      onConfirm: async () => { setConfirm(null); await runSectionSparkle(page, section); },
    });
  };

  const askDeletePage = (p) => setConfirm({
    title: "Delete this page?",
    message: `"${p.title || "Untitled page"}" will be removed from your site plan. The copy Brill wrote for it in My Website isn't touched — you can add the page again from the Add page menu.`,
    confirmLabel: "Delete",
    danger: true,
    onConfirm: async () => {
      const ok = await persistDeletePage(p.id);
      setConfirm(null);
      // Clearing the selection lets Page copy fall back to her first page.
      if (ok) { setEditingPage(null); go("website"); }
      /* An offer that sent people here now sends them nowhere — Postgres nulled
         `page_id` (0018), our copy of the offer has not heard. */
      if (ok && offersPointingAt(offerStore.offers, [p.id]).length) offerStore.reload?.();
    },
  });

  const [editingOffer, setEditingOffer] = useState(null);
  const [savingOffer, setSavingOffer] = useState(false);
  const [confirm, setConfirm] = useState(null);
  // When the sidebar drives the selection, follow it (drill-downs still use local state).
  useEffect(() => { if (panel) { setActive(panel); setMenuOpen(false); } }, [panel]);

  /* Offers come from Supabase via App. The once-ever pull from the Accelerator
     used to live here as an effect; it now lives in `useOffers` alongside the
     seeded flag, because that flag moved to `settings.offers_seeded` and the pull
     has to be able to await the save before recording that it happened. */
  const {
    offers = [], loading: offersLoading = false, error: offersError = null,
    clearError: clearOffersError,
    saveOffer: persistOffer, deleteOffer: persistDeleteOffer,
    newOfferId, pullSignatureOffer: persistPull, resyncOffer: persistResync,
  } = offerStore;

  /* GO TELLS THE SIDEBAR. Her report, 2026-10-01: from Social, the Library
     button in the header opened the Library "on top of" Social and there was
     no way back — clicking Social did nothing; only Newsletters and then Social
     again closed it. `go` moved only this screen, so the sidebar still held
     Social, and choosing Social re-selected what it already had: `panel` never
     changed and the effect above never fired. So every `go` to a panel the nav
     lists also moves the sidebar, through the same `onOpenModule` App already
     hands down, with the nav's own id (`route`, else `m/<id>` — Sidebar's
     toolNavId). Panels the nav does not list stay local. */
  const go = (id) => {
    setActive(id);
    setMenuOpen(false);
    const nav = navIdFor(id);
    if (nav) onOpenModule?.(nav);
  };

  /* NO SEPARATE `offer_form` ROUTE ANY MORE, the same change Website made when
     `website_page` went: editing an offer is the Offer view with that offer
     selected, so there is ONE editing screen rather than two that differ only
     by whether the ladder is beside them. */
  const [offerView, setOfferView] = useState("table");
  /* Form / Brainstorm, held here rather than in OfferForm so OfferCopy can
     stand its chrome down while she brainstorms — brainstorming is a drafting
     screen (2026-09-24). Back to the form whenever she opens another offer:
     the id is minted at Add, so a first save does not reset it mid-chat. */
  const [offerMode, setOfferMode] = useState("form");
  useEffect(() => { setOfferMode("form"); }, [editingOffer?.id]);
  const startAddOffer = (type) => {
    /* AT THE END OF ITS RUNG, not the top. Once anything has been dragged the
       rung holds real numbers, and a new offer defaulting to 0 would jump above
       the order she just set. Before any drag everything is 0 and the tie
       breaks on created_at, so this is the same place it always landed. */
    const last = offers.length ? Math.max(...offers.map((o) => o.sortOrder ?? 0)) : -1;
    setEditingOffer({
      id: newOfferId(), type, name: "", pricing: { ...EMPTY_PRICING }, summary: "",
      status: "draft", sortOrder: last + 1, isNew: true,
    });
    setOfferView("copy"); setMenuOpen(false);
  };
  /* DRAGGING A RUNG INTO HER ORDER (her ask, 2026-09-17). It lives here rather
     than in `useOffers` because the banding is `groupOffers`, which is a
     component's export — a store reaching into the panel it backs is the wrong
     way round. Sequential, like every other reorder: the store replaces rows one
     at a time and racing them can interleave writes onto a stale list. */
  const reorderOffers = async (band, from, to) => {
    let ok = true;
    for (const row of offerReorderWrites(offers, band, from, to)) {
      if (!(await persistOffer(row))) ok = false;
    }
    return ok;
  };
  const openOffer = (o) => { setEditingOffer({ ...o }); setOfferView("copy"); };
  /* THE TOGGLE, not a plain setState: switching to `Offer` with nothing picked
     showed the rail beside an empty form headed "New other offer", which reads
     as her offer having vanished (her report, 2026-09-17). It opens the first
     offer in LADDER order — the rail's own order, so the one that opens is the
     one at the top of the list she is looking at.

     It seeds `editingOffer` rather than deriving it, because Offers hold a DRAFT
     COPY of the record while she edits (every other panel keeps an id), and a
     fallback computed on each render would be a new object every keystroke. */
  const goOfferView = (v) => {
    if (v === "copy" && !editingOffer) {
      const first = groupOffers(offers).flatMap((g) => g.rows)[0];
      if (first) setEditingOffer({ ...first });
    }
    setOfferView(v);
  };

  /* Every write below can fail, and on failure NOTHING was saved — there is no
     localStorage fallback by design. So each one bails before closing the form:
     leaving her on the form with the error visible keeps her edits recoverable,
     where navigating away would discard them and imply the save worked. */
  /* `patch` is how a CHOICE saves — a status pill, a Free toggle, a select.
     setDraft is not synchronous, so a control that sets and then saves would
     write the value from before the click; the patch carries the new one. Same
     shape as `savePage(status)`, and the same lesson behind it.

     It no longer navigates away. The form saves on the way out of every field
     now (rule 1), so closing the screen on a save would throw her out of the
     offer on the first blur. */
  const saveOffer = async (patch, { justMade = null } = {}) => {
    if (!editingOffer) return;
    const { isNew, ...merged } = { ...editingOffer, ...(patch || {}) };
    /* `cta_id` and `page_id` are real foreign keys. An id that no longer
       resolves (deleted in another tab) is dropped rather than failing the
       whole save — except the one just made, which our lists can't hold yet. */
    const o = dropDeadIds(merged, { ctas, pages: offerablePages, justMade });
    /* Most blurs have nothing to write. Comparing is cheaper than a round trip,
       and without it tabbing across the name and the summary writes the row
       twice — the same guard `savePage` carries. */
    const stored = offers.find((x) => x.id === o.id);
    if (!isNew && stored && JSON.stringify({ ...stored, updatedAt: 0 }) === JSON.stringify({ ...o, updatedAt: 0 })) return;
    setSavingOffer(true);
    const saved = await persistOffer(o);
    setSavingOffer(false);
    if (!saved) return;
    /* Stays on the offer she is writing. Failure keeps the draft on screen with
       the banner, which is what makes her edits recoverable. */
    setEditingOffer((d) => (d && d.id === o.id ? { ...saved } : d));
  };
  /* A row under "Supported by" opens the thing it names, on its own screen. */
  const openSupporter = (kind, r) => {
    /* The post itself now, not just the screen: Social is a panel in here. */
    if (kind === "posts") { onOpenModule?.("social_generator"); openPost(r); return; }
    if (kind === "pages") {
      const siteId = r.siteId || sites[0]?.id || null;
      setOpenSiteId(siteId); setSiteCog(false); setEditingPage({ ...r }); setDraftingSectionId(null); setWebsiteView("copy"); go("website");
      return;
    }
    if (kind === "sites") { setOpenSiteId(r.id); setSiteCog(false); setEditingPage(null); setDraftingSectionId(null); setWebsiteView("copy"); go("website"); }
    else if (kind === "courses") { setOpenCourseId(r.id); setOpenModuleId(null); setCourseCog(false); setCourseView("copy"); go("lesson_plan"); }
    else if (kind === "sequences") { openSequence(r); go("outbound"); }
    else if (kind === "scripts") { openScript(r); go("salescall"); }
    else if (kind === "presentations") { openDeck(r); go("talks"); }
  };
  /* Until supabase/0018 runs, `offers` has no `page_id` or `link_url`, and
     choosing a page or a URL is refused with a Postgres sentence about a column.
     Said in words she can act on instead. */
  const offerSaveError = offersError && /page_id|link_url/.test(offersError)
    ? "Sending an offer to a page or a URL needs a database update first: supabase/0018_offer_destination.sql."
    : offersError;
  const removeOffer = async () => {
    if (!(await persistDeleteOffer(editingOffer.id))) return;
    setEditingOffer(null); setOfferView("table");
  };

  /* ── Making a destination from inside the offer ──────────────────────────
     The two "make one" rows in the offer's Where-it-sends-people picker. Both
     return true only when the offer now points at the new thing, so the picker
     can stay open on a failure rather than closing as though it worked.

     A NEW MEETING LINK is a real row in Run → Meeting Links, not a copy on the
     offer: edit it there once and every offer and post using it follows. Only
     the two fields that make it usable are asked for; the rest wait in Run. */
  const newMeetingLinkFor = async (offer, { buttonText, link }) => {
    const cta = await persistCta({ name: buttonText, buttonText, link, type: "call", status: link ? "live" : "draft" });
    if (!cta) return false;
    await saveOffer(pointAt("cta", cta.id), { justMade: cta.id });
    return true;
  };

  /* A LANDING PAGE FOR THIS OFFER. One landing site per offer, made the first
     time and reused after — pressing this twice fills one site rather than
     making two. The site carries the offer (`sites.offer_id`, the connector),
     and is named after it, because "New landing site" in a switcher tells her
     nothing about which offer it sells.

     Written straight through the page store rather than `addStandardPage`,
     which only ever adds to the site open on the Sites screen — and she is on
     the Offer Ladder, where no site is open. If the site already has its
     landing page, that page is the answer; a second /landing in one site would
     be refused by the address rule anyway. */
  const makeLandingPageFor = async (offer) => {
    if (!offer?.id) return false;
    let target = landingSiteFor(offer, sites);
    if (!target) {
      const made = await addSite("landing");
      if (!made) return false;
      target = await saveSite({ ...made, name: String(offer.name || "").trim() || made.name, offerId: offer.id });
      if (!target) return false;
    }
    const def = standardPagesFor("landing")[0];
    const siblings = pagesOfSite(allPages, target.id, sites[0]?.id === target.id);
    let page = siblings.find((p) => p.kind === def.kind || p.slug === def.slug);
    if (!page) {
      page = await persistPage({
        id: newPageId(), siteId: target.id,
        slug: def.slug, title: def.title, kind: def.kind,
        sourceSection: def.sourceSection, source: def.sourceSection ? WEBSITE_SOURCE : null,
        status: "draft", sortOrder: nextOrder(siblings),
      });
      if (!page) return false;
    }
    await saveOffer(pointAt("page", page.id), { justMade: page.id });
    return true;
  };

  /* The automatic pull is once-ever, which leaves it unreachable afterwards —
     nothing brings the row back if she deletes it, and nothing retries for
     anyone whose flag was already set before they locked My Offer. This is that
     route in. Additive and non-destructive, so unlike Resync it needs no
     confirm. Offered only when there's something to pull and no pulled row
     already exists; that second case is Resync's job, not a second copy. */
  const alreadyPulled = offers.some((o) => o.source === "accelerator");
  const canPull = !alreadyPulled && hasSignatureOffer(offerOutputs);
  const doPull = async () => { setMenuOpen(false); await persistPull(); };

  // Delete straight from the table. Confirmed first — unlike the form's Delete,
  // there's no open record in front of her to make the target obvious.
  const askDeleteRow = (o) => setConfirm({
    title: "Delete this offer?",
    message: `"${o.name || "Untitled offer"}" will be removed from your ladder. This can't be undone. To keep the record but stop selling it, open it and set the status to Retired instead.`,
    confirmLabel: "Delete",
    danger: true,
    onConfirm: async () => { await persistDeleteOffer(o.id); setConfirm(null); },
  });

  /* Resync is the only thing that overwrites her edits, so it is always opt-in
     and always warns first. The row flags itself when the source moves; this is
     the action she takes from that flag. */
  const askResync = (o) => setConfirm({
    title: "Pull the new version?",
    message: "This replaces this offer's name, description and pricing with what's in My Offer now. Anything you've edited here will be lost. Your offer type, where it sends people and its status are kept.",
    confirmLabel: "Resync",
    danger: true,
    onConfirm: async () => {
      // `persistResync` returns the saved offer itself, not the whole list —
      // null means the write failed and nothing changed.
      const saved = await persistResync(o);
      setConfirm(null);
      if (!saved) return;
      setEditingOffer((d) => (d && d.id === saved.id ? { ...saved } : d));
    },
  });
  // Looks a tool up in BUCKETS — still used to title the coming-soon panels.
  const findItem = (id) => {
    for (const bk of BUCKETS) { const it = bk.items.find((i) => i.id === id); if (it) return { bk, it }; }
    return null;
  };

  /* The coming-soon screen, for any tool that is not live for this person. */
  const soonScreen = (id) => {
    const [b, d] = SOON_COPY[id] || ["Momentum", "Coming soon."];
    const label = findItem(id)?.it.label || id;
    return (
      <>
        <Bread text={b} />
        <h2 style={pTitle}>{label}</h2>
        <div style={soonCard}>
          <div style={{ fontSize: 26 }}>🧩</div>
          <div><h3 style={{ margin: "0 0 6px", fontSize: 15 }}>{label}</h3>
            <p style={{ margin: 0, color: B.mid, fontSize: 13, lineHeight: 1.6 }}>{d}</p>
            <SoonBadge />
          </div>
        </div>
      </>
    );
  };

  /* The hub's own bucket nav lived here, along with the breadcrumb map that fed it.
     The sidebar's phase switcher owns Momentum's tree now and App always mounted
     this with `hideNav`, so both were unreachable — removed rather than migrated.
     Each panel writes its own <Bread> text. */

  /* ── The Level 1 switchers ────────────────────────────────────────────────
     Built here rather than in the panels, because the panels are not allowed to
     know what a site or a course IS — shell.jsx renders the dropdown, this says
     what goes in it.

     THE COG IS THE ONLY WAY TO RENAME OR DELETE ONE (rule 8). Everything behind
     it is set once and then read, so it does not earn a form field in front of
     the work. Deleting takes the children with it, which is why the confirm
     counts them by name rather than saying "and its pages". */
  const askDeleteSite = (s) => setConfirm({
    title: "Delete this site?",
    message: pages.length
      ? `"${siteLabel(s)}" and its ${pages.length} page${pages.length === 1 ? "" : "s"} will be removed, along with every section written in them. This can't be undone.`
      : `"${siteLabel(s)}" will be removed. It has no pages in it.`,
    confirmLabel: "Delete",
    danger: true,
    onConfirm: async () => {
      const ok = await deleteSite(s.id);
      setConfirm(null);
      /* Clearing the selection lets the switcher fall back to her first site.
         The open page has to go too — it was a page of the site just deleted,
         and Postgres has already taken it. */
      if (ok) { setOpenSiteId(null); setSiteCog(false); setEditingPage(null); }
      // Its pages went with it, and any offer pointing at one of them.
      // Read by the site's id, not `pages`: this is also reached from the
      // all-sites table, where the site being deleted is not the open one.
      if (ok && offersPointingAt(offers, pagesOf(s.id).map((p) => p.id)).length) offerStore.reload?.();
    },
  });

  const askDeleteCourse = (c) => setConfirm({
    title: "Delete this course?",
    message: courseModules.length
      ? `"${courseLabel(c)}" and its ${courseModules.length} module${courseModules.length === 1 ? "" : "s"} will be removed, along with everything written in them. This can't be undone.`
      : `"${courseLabel(c)}" will be removed. It has no modules in it.`,
    confirmLabel: "Delete",
    danger: true,
    onConfirm: async () => {
      const ok = await deleteCourse(c.id);
      setConfirm(null);
      if (ok) { setOpenCourseId(null); setCourseCog(false); setOpenModuleId(null); }
    },
  });

  /* The offer picker behind both cogs. */
  const offerOptions = [["", "No offer"], ...offers.map((o) => [o.id, (o.name || "Untitled offer") + (o.status === "retired" ? " (retired)" : "")])];

  const siteSwitcher = (
    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
      <L1Switcher noun="site" items={sites} selectedId={site?.id} labelFor={siteLabel} loading={sitesLoading}
        onSelect={(s) => { setOpenSiteId(s.id); setSiteCog(false); setEditingPage(null); }}
        addOptions={SITE_KINDS.map((k) => ({ id: k.id, label: k.label, hint: k.hint }))}
        onAdd={async (kind) => {
          const made = await addSite(kind);
          /* Straight into its settings: it is called "New site" until she says
             otherwise, and the cog is the only place that can be changed. */
          if (made) { setOpenSiteId(made.id); setEditingPage(null); setSiteCog(true); }
        }} />
      {site && <IconBtn icon={Settings} label={siteCog ? "Hide site settings" : "Site settings"} onClick={() => setSiteCog((v) => !v)} />}
      {/* Brill writes this site's pages for its offer (utils/pageOffer); with none
          linked it writes from her Accelerator offer, whatever the site is for.
          LANDING SITES ONLY: a landing site is "one offer, one page, one action"
          (SITE_KINDS), while a main site sells the whole business, and nudging
          her to pin one offer on it would have Brill write her homepage about
          that one offer. */}
      {site && !siteCog && siteKind(site) === LANDING_SITE && !offers.some((o) => o.id === site.offerId) && <CogNudge label="Link your offer" onClick={() => setSiteCog(true)} />}
    </div>
  );

  const siteDrawer = site && siteCog ? (
    <L1Settings key={site.id} noun="site" record={site} onSave={saveSite}
      fields={[
        { key: "name", label: "Site name", placeholder: "Name this site" },
        { key: "domain", label: "Where it lives", placeholder: "tevrawood.com", width: 200,
          hint: "A label for the handoff, not something Brill publishes to." },
        /* The connector (locked 2026-08-22): what this site is for. It is what
           puts the site under "Supported by" on that offer. */
        { key: "offerId", label: "Offer it serves", width: 220, options: offerOptions },
      ]}
      /* The last site has no Delete: there would be nowhere for her pages to
         live and nothing for the switcher to fall back to. */
      onDelete={sites.length > 1 ? () => askDeleteSite(site) : null}
      deleteLabel="Delete this site" />
  ) : null;

  const courseSwitcher = (
    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
      <L1Switcher noun="course" items={courses} selectedId={course?.id} labelFor={courseLabel} loading={coursesLoading}
        onSelect={(c) => { setOpenCourseId(c.id); setCourseCog(false); setOpenModuleId(null); }}
        addOptions={[{ id: "course", label: "New course", hint: "A second thing you teach, with its own modules" }]}
        onAdd={async () => {
          const made = await addCourse();
          if (made) { setOpenCourseId(made.id); setOpenModuleId(null); setCourseCog(true); }
        }} />
      {course && <IconBtn icon={Settings} label={courseCog ? "Hide course settings" : "Course settings"} onClick={() => setCourseCog((v) => !v)} />}
      {course && !courseCog && !offers.some((o) => o.id === course.offerId) && <CogNudge label="Link your offer" onClick={() => setCourseCog(true)} />}
    </div>
  );

  const courseDrawer = course && courseCog ? (
    <L1Settings key={course.id} noun="course" record={course} onSave={saveCourse}
      fields={[
        { key: "title", label: "Course name", placeholder: "Name this course" },
        { key: "offerId", label: "Offer it delivers", width: 220, options: offerOptions },
      ]}
      onDelete={courses.length > 1 ? () => askDeleteCourse(course) : null}
      deleteLabel="Delete this course" />
  ) : null;

  // ── Panels ────────────────────────────────────────────────────────────────────
  const renderPanel = () => {
    switch (active) {
      /* Pressing `Offer` with nothing picked used to land her on an empty form
         headed "New other offer" (her report, 2026-09-17). The toggle now opens
         the first offer in LADDER order — the rail's own order — the same
         fallback `copyScript` and `copyDeck` make. */
      case "offer_ladder":
        if (offerView === "copy") {
          return (
            <OfferCopy offers={offers} selectedId={editingOffer?.id} offer={editingOffer}
              view={offerView} setView={goOfferView} onReorder={reorderOffers}
              drafting={offerMode === "brainstorm"}
              onBack={() => { setEditingOffer(null); setOfferView("table"); }}
              onSelect={(o) => setEditingOffer({ ...o })}>
              <OfferForm draft={editingOffer} setDraft={setEditingOffer} onSave={saveOffer}
                onDelete={removeOffer} onResync={askResync} saving={savingOffer}
                onBack={() => { setEditingOffer(null); setOfferView("table"); }}
                isMobile={isMobile} offerOutputs={offerOutputs}
                offers={offers} ctas={ctas} chatContext={momentumChatContext}
                pages={offerablePages} sites={sites}
                pageState={(p) => pageState(p, sectionsFor(p.id))}
                onMakeLandingPage={makeLandingPageFor} onNewMeetingLink={newMeetingLinkFor}
                error={offerSaveError} onDismissError={clearOffersError}
                supporters={supportersOf(editingOffer, { sites, pages: offerablePages, courses, sequences, scripts: callScripts, presentations: isAdmin ? presentations : [], posts: socialPosts, pagesOf, modulesOf, sequenceName: sequenceLabel })}
                onOpenSupporter={openSupporter}
                mode={offerMode} setMode={setOfferMode} />
            </OfferCopy>
          );
        }
        return <OfferLadder offers={offers} ctas={ctas} pages={offerablePages} sites={sites} onAdd={startAddOffer} onOpen={openOffer} onDelete={askDeleteRow} menuOpen={menuOpen} setMenuOpen={setMenuOpen} offerOutputs={offerOutputs} canPull={canPull} onPull={doPull} loading={offersLoading} error={offersError} onRetry={offerStore.reload} onDismissError={clearOffersError} view={offerView} setView={goOfferView} onReorder={reorderOffers} />;
      /* SOCIAL (spec phase A1, 2026-09-24): a Momentum panel now, reached from
         the same "Social" row in the nav as the old Social Generator. */
      case "social": {
        const socialPillars = parsePillars(socialContext?.contentPillars);
        if (postView === "copy" && editingPost) {
          /* A post in an arc goes back to ITS ARC — one way back (rule 9c). */
          const arcOf = editingPost.arcId ? socialArcs.find((a) => a.id === editingPost.arcId) || null : null;
          const slotN = Number(editingPost.inputs?.arcSlot) || null;
          const back = () => {
            setEditingPost(null);
            if (arcOf) { setOpenArcId(arcOf.id); setPostView("arc"); } else setPostView("table");
          };
          return (
            <SocialCopy>
              {/* The post's name reads as the heading and is typed there (rule 7).
                  Brill names it from the first line when it drafts, if she has not.
                  DONE sits beside it, because there is no rail to carry the dot
                  (her call, 2026-09-25) — and, as on a website page, a post with
                  no words has nothing to be Done about. PostEditor draws these
                  rows (2026-10-06) so the phone can lay them out its own way. */}
              <PostEditor key={editingPost.id} draft={editingPost} setDraft={setEditingPost} onSave={savePost}
                onDelete={askDeletePost} saving={savingPost} error={postsError} onDismissError={clearPostsError}
                posts={socialPosts} offers={offers} ctas={ctas} socialContext={socialContext || {}} userSettings={userSettings}
                arc={arcOf && slotN ? arcContext(arcOf, socialPosts, slotN) : null}
                newPostId={newPostId} onCreatePost={storePost} onOpenPost={openPost}
                back={{ label: arcOf ? "← The arc" : "← All posts", onClick: back }}
                titleField={{ value: editingPost.title || "", onChange: (v) => setEditingPost((d) => ({ ...d, title: v })), onCommit: () => savePost(editingPost) }}
                status={!editingPost.isNew && postState(editingPost) !== "empty" && (
                  <SectionStatus value={editingPost.status === "done" ? "done" : "draft"}
                    onChange={(status) => markPost(editingPost, status)} />
                )}
                note={arcOf && slotN && (
                  <span style={{ fontSize: 12.5, color: B.dim, flexBasis: "100%" }}>
                    Post {slotN} of {arcRows(arcOf, socialPosts).total} in {arcOf.name || "this arc"}{editingPost.scheduledDate ? ` · ${dayLabel(editingPost.scheduledDate)}` : ""}
                  </span>
                )} />
            </SocialCopy>
          );
        }
        const openArcRec = postView === "arc" ? socialArcs.find((a) => a.id === openArcId) || null : null;
        if (openArcRec) {
          return (
            <ArcScreen key={openArcRec.id} arc={openArcRec} posts={socialPosts} pillars={socialPillars} offers={offers} ctas={ctas}
              socialContext={socialContext || {}} autoPlan={autoPlanArcId === openArcRec.id} newPostId={newPostId}
              onSaveArc={persistArc} onStorePost={storePost}
              onOpenPost={(p) => { setEditingPost({ ...p, ...(socialPosts.some((x) => x.id === p.id) ? {} : { isNew: true }) }); setPostView("copy"); }}
              onDeletePost={askDeletePost} onDeleteArc={askDeleteArc}
              onBack={() => { setOpenArcId(null); setPostView("table"); }}
              confirm={setConfirm} error={arcsError || postsError} onDismissError={() => { clearArcsError(); clearPostsError(); }} />
          );
        }
        return (
          <>
            <SocialTable posts={socialPosts} arcs={socialArcs} loading={postsLoading || arcsLoading} error={postsError || arcsError}
              onDismissError={() => { clearPostsError(); clearArcsError(); }}
              onOpen={openPost} onDelete={askDeletePost} onAdd={startAddPost}
              onAddArc={() => { clearArcsError(); setArcSetupOpen(true); }} onOpenArc={openArc} onDeleteArc={askDeleteArc}
              onMark={markPost} />
            {arcSetupOpen && (
              <ArcSetup pillars={socialPillars} offers={offers.filter((o) => o.status !== "retired")} ctas={ctas}
                busy={creatingArc} error={arcsError}
                onCancel={() => setArcSetupOpen(false)} onPlan={createArc} />
            )}
          </>
        );
      }
      /* `cta_form` was its own screen until 2026-09-23. Kept as a way in, so
         anything still sending her there lands on the one editing screen. */
      case "cta_form":
      case "ctas":
        if (ctaView === "copy" && editingCta) {
          const back = () => { setEditingCta(null); setCtaView("table"); };
          return (
            <CtaCopy ctas={ctas} selectedId={editingCta.id} cta={editingCta}
              view={ctaView} setView={goCtaView} onBack={back}
              onSelect={(c) => setEditingCta({ ...c })}>
              <CtaForm key={editingCta.id} draft={editingCta} setDraft={setEditingCta} onSave={saveCta}
                onDelete={askDeleteCta} saving={savingCta}
                error={ctasError} onDismissError={clearCtasError} />
            </CtaCopy>
          );
        }
        return <CtaLibrary ctas={ctas} offers={offers} loading={ctasLoading} error={ctasError} onDismissError={clearCtasError} menuOpen={menuOpen} setMenuOpen={setMenuOpen} openForm={startAddCta} onOpen={openCta} onDelete={askDeleteCta} view={ctaView} setView={goCtaView} />;
      case "lesson_plan":
        /* The landing screen is every COURSE, not every module — so it is not
           inside CourseShell, which is the chrome for being IN one. */
        if (courseView !== "copy") {
          return (
            <CoursesTable courses={courses} modulesOf={modulesOf}
              loading={coursesLoading} error={coursesError} onDismissError={clearCoursesError}
              /* The cog closes on the way in, same as Sites. It is left OPEN
                 deliberately when she adds a course — that is where the name
                 goes — and without this, the next course she opens from the
                 table also lands with a settings card between the bar and her
                 modules, which reads as the screen rather than as a drawer she
                 left open. She hit exactly that: "this name and settings is
                 HUGE", then "oh that is settings". */
              onOpen={(c) => { setOpenCourseId(c.id); setOpenModuleId(null); setCourseCog(false); setCourseView("copy"); }}
              onDelete={courses.length > 1 ? askDeleteCourse : null}
              onReorder={reorderCourses}
              onAdd={async () => {
                const made = await addCourse();
                if (made) { setOpenCourseId(made.id); setOpenModuleId(null); setCourseCog(true); setCourseView("copy"); }
              }} />
          );
        }
        return (
          <CourseShell modules={courseModules}
            onBackToCourses={() => setCourseView("table")} courseName={courseLabel(course)}
            rung={rungTag(course, offers, "asset")}
            error={courseError || coursesError || draftError}
            onDismissError={() => { clearCourseError(); clearCoursesError(); setDraftError(null); }}
            onAdd={addAndOpenModule} canPull={canPullCurriculum} onPull={doPullCurriculum} seeded={courseSeeded}
            notice={courseNotice} onDismissNotice={() => setCourseNotice(null)}
            level1={courseSwitcher} drawer={courseDrawer}
            draftingBack={draftingBlockId && copyModule
              ? { label: MODULE_BACK, onClick: () => setDraftingBlockId(null) } : null}>
            {courseView === "copy" ? (
              <CourseCopy modules={courseModules} onExitToCourses={() => setCourseView("table")} selectedId={copyModule?.id}
                drafting={!!draftingBlockId && !!copyModule}
                onReorder={reorderModules} onMark={markModule}
                onSelect={(m) => { setDraftingBlockId(null); setDraftingWholeModule(false); setOpenModuleId(m.id); }}>
                {draftingBlockId && copyModule ? (
                  <CourseDrafter key={`${copyModule?.id}:${draftingBlockId}:${draftingWholeModule ? "whole" : "one"}`} module={copyModule} kindId={draftingBlockId}
                    startScope={draftingWholeModule ? "record" : "block"}
                    onGoToBlock={(id) => { setDraftingWholeModule(false); setDraftingBlockId(id); }}
                    chatContext={momentumChatContext} onSave={saveModule} onBack={() => setDraftingBlockId(null)}
                    /* The offer this COURSE delivers (the course holds it, never a module). */
                    offer={offers.find((o) => o.id === course?.offerId) || null}
                    /* The next module, once this one has nothing left to write:
                       its first unwritten block, or its page if it has none. */
                    nextRecord={(() => {
                      const next = courseModules[copyModuleIndex + 1];
                      if (!next) return null;
                      return {
                        label: next.title || "Untitled module", noun: "module",
                        go: () => {
                          const blocks = blocksOf(next);
                          const target = blocks.find((k) => blockWords(next, k.id) === 0) || blocks[0];
                          setOpenModuleId(next.id);
                          /* An empty module opens on its outcome, in whichever
                             scope she is in. */
                          setDraftingBlockId(target ? target.id : "outcome");
                        },
                      };
                    })()} />
                ) : (
                  /* Keyed by the module, so switching modules in the rail remounts
                     the editor. Its blocks each hold a DRAFT of their own content;
                     without this, module A's unsaved keystrokes would appear in
                     module B's fields. */
                  <CourseModule key={copyModule?.id} module={copyModule} index={copyModuleIndex}
                    error={courseError} onBackToTable={() => setCourseView("table")}
                    onSave={saveModule} onDelete={askDeleteModule} onAddBlock={addBlock} onRemoveBlock={askClearBlock}
                    /* The whole module, decision 6. The block it lands on is
                       the first with no words, for when she switches to one. */
                    onOpenChat={() => {
                      const blocks = blocksOf(copyModule);
                      const target = blocks.find((k) => blockWords(copyModule, k.id) === 0) || blocks[0];
                      setDraftingWholeModule(true);
                      setDraftingBlockId(target ? target.id : "outcome");
                    }}
                    onDraft={setDraftingBlockId} onSparkle={askSparkle} sparkleId={sparkleId} drafted={drafted} />
                )}
              </CourseCopy>
            ) : null}
          </CourseShell>
        );
      case "outbound": {
        const seqTouches = copySequence ? touchesFor(copySequence.id) : [];
        const draftingTouch = seqTouches.find((t) => t.id === draftingTouchId) || null;
        return (
          <OutreachShell sequences={sequences} view={outreachView} setView={setOutreachView}
            error={outreachError || outreachDraftError}
            onDismissError={() => { clearOutreachError(); setOutreachDraftError(null); }}
            menuOpen={menuOpen} setMenuOpen={setMenuOpen} onAdd={addAndOpenSequence}
            draftingBack={draftingTouch ? { label: TOUCH_BACK, onClick: () => setDraftingTouchId(null) } : null}>
            {outreachView === "copy" ? (
              <OutreachCopy sequences={sequences} touchesFor={touchesFor} selectedId={copySequence?.id}
                onExitToTable={() => setOutreachView("table")}
                onReorder={reorderSequences} onMark={markSequence} drafting={!!draftingTouch}
                onSelect={(s) => { setDraftingTouchId(null); setOpenSequenceId(s.id); }}>
                {/* Keyed by the sequence, so switching in the rail remounts the
                    editor. Its name, goal and touches each hold a DRAFT of their
                    own value; without this, sequence A's unsaved keystrokes would
                    appear in sequence B's fields. */}
                {draftingTouch ? (
                  /* Writing one touch with Brill — the shared drafting screen.
                     Keyed by the touch: it holds that touch's words from the
                     moment it mounts (the website learned this, spec 5.4). */
                  <OutreachDrafter key={`${draftingTouch.id}:${draftingWhole ? "whole" : "one"}`} sequence={copySequence} touch={draftingTouch}
                    startScope={draftingWhole ? "record" : "block"}
                    touches={seqTouches} offers={offers} chatContext={momentumChatContext}
                    onSave={saveTouch} onBack={() => setDraftingTouchId(null)}
                    /* The next sequence, in the same scope she is in. */
                    nextRecord={(() => {
                      const i = sequences.findIndex((x) => x.id === copySequence?.id);
                      const next = i >= 0 ? sequences[i + 1] : null;
                      if (!next) return null;
                      return {
                        label: sequenceLabel(next), noun: "sequence",
                        go: () => {
                          const ts = touchesFor(next.id);
                          const target = ts.find((t) => !String(t.body || "").trim()) || ts[0];
                          setOpenSequenceId(next.id);
                          setDraftingTouchId(target ? target.id : null);
                        },
                      };
                    })()}
                    onGoToTouch={(id) => { setDraftingWhole(false); setDraftingTouchId(id); }} />
                ) : (
                  <OutreachSequence key={copySequence?.id} sequence={copySequence}
                    touches={seqTouches} offers={offers} brand={emailBrand}
                    error={outreachError} onSave={saveSequence} onDelete={askDeleteSequence}
                    onSaveTouch={saveTouch} onClearTouch={askClearTouch}
                    onSparkleTouch={(t) => askTouchSparkle(copySequence, t, seqTouches)}
                    sparklingTouchId={sparklingTouchId} draftedTouch={draftedTouch}
                    onAddTouch={addTouch} onRemoveTouch={askRemoveTouch} onReorderTouches={reorderTouches}
                    onDraftTouch={(t) => { setDraftingWhole(false); setDraftingTouchId(t.id); }}
                    /* Opens on the first touch with no words, like "Write this
                       page with Brill" opens on the page's first section. */
                    /* The whole sequence, decision 6. The touch it lands on is
                       the first with no words, for when she switches to one. */
                    onWrite={seqTouches.length ? () => { setDraftingWhole(true); setDraftingTouchId((seqTouches.find((t) => !String(t.body || "").trim()) || seqTouches[0]).id); } : null}
                    onBackToTable={() => setOutreachView("table")} />
                )}
              </OutreachCopy>
            ) : (
              <OutreachTable sequences={sequences} touchesFor={touchesFor} offers={offers}
                loading={outreachLoading} onOpen={openSequence} onDelete={askDeleteSequence}
                onReorder={reorderSequences} />
            )}
          </OutreachShell>
        );
      }
      case "talks": {
        // Everyone but the admin gets the coming-soon screen (toolStatus).
        if (toolStatus(findItem("talks")?.it, isAdmin) !== "live") return soonScreen("talks");
        const deckSections = copyDeck ? deckSectionsFor(copyDeck.id) : [];
        const deckSlideRows = copyDeck ? deckSlidesFor(copyDeck.id) : [];
        const slideNow = draftingSlide ? deckSlideRows.find((s) => s.id === draftingSlide.slideId) : null;
        const sectionNow = slideNow ? deckSections.find((s) => s.id === slideNow.sectionId) || null : null;
        return (
          <PresentationsShell view={decksView} setView={setDecksView} onBackToDecks={() => setDecksView("table")}
            error={decksError || slideDraftError}
            onDismissError={() => { clearDecksError(); setSlideDraftError(null); }}
            menuOpen={menuOpen} setMenuOpen={setMenuOpen} onAdd={addAndOpenDeck}
            draftingBack={slideNow ? { label: DECK_BACK, onClick: () => setDraftingSlide(null) } : null}>
            {/* The rail is THIS deck's slides (her call, 2026-09-17). Picking
                one opens it in place and scrolls to it; switching deck is the
                Table, a level up. */}
            {decksView === "copy" ? (
              <PresentationsCopy sections={deckSections} slides={deckSlideRows} onBackToDecks={() => setDecksView("table")}
                selectedSlideId={pickedSlideId} drafting={!!slideNow}
                onSelectSlide={(s) => setPickedSlide({ id: s.id, n: (pickedSlide?.n || 0) + 1 })}>
                {slideNow ? (
                  <PresentationDrafter key={`${slideNow.id}:${draftingSlide.whole ? "whole" : "one"}`}
                    deck={copyDeck} section={sectionNow} slide={slideNow}
                    sections={deckSections} slides={deckSlideRows} offers={offers}
                    chatContext={momentumChatContext} startScope={draftingSlide.whole ? "record" : "block"}
                    onSave={saveSlide} onBack={() => setDraftingSlide(null)}
                    onGoToSlide={(id) => setDraftingSlide({ slideId: id, sectionId: sectionNow?.id, whole: false })} />
                ) : (
                  <PresentationDeck key={copyDeck?.id} deck={copyDeck} sections={deckSections} slides={deckSlideRows}
                    offers={offers} voiceOutputs={acceleratorOutputs?.voice || {}} brand={userSettings?.brand} error={decksError}
                    onSave={savePresentation} onDelete={askDeleteDeck}
                    onSaveSection={saveDeckSection} onAddSection={addDeckSection} onDeleteSection={askDeleteDeckSection}
                    onSaveSlide={saveSlide} onRemoveSlide={askRemoveSlide}
                    onSparkleSlide={(section, slide) => askSlideSparkle(copyDeck, section, slide, deckSections, deckSlideRows)}
                    sparklingSlideId={sparklingSlideId} draftedSlide={draftedSlide}
                    pickedSlideId={pickedSlideId}
                    onAddSlides={(section, rows) => addSlides(copyDeck, section, rows)}
                    onReorderSlides={(section, from, to) => reorderSlides(copyDeck, section, from, to)}
                    onDraftSlide={(section, s) => setDraftingSlide({ slideId: s.id, sectionId: section?.id, whole: false })}
                    onDraftSection={(section, first) => setDraftingSlide({ slideId: first.id, sectionId: section.id, whole: true })}
                    proposals={proposals}
                    onPropose={(section) => runProposal(copyDeck, section)}
                    onAcceptProposal={(section) => acceptProposal(copyDeck, section)}
                    onDismissProposal={dismissProposal} />
                )}
              </PresentationsCopy>
            ) : (
              <PresentationsTable presentations={presentations} slidesFor={deckSlidesFor}
                loading={decksLoading} onOpen={openDeck} onDelete={askDeleteDeck}
                onReorder={reorderPresentations} />
            )}
          </PresentationsShell>
        );
      }
      case "salescall": {
        const scriptSections = copyScript ? scriptSectionsFor(copyScript.id) : [];
        const draftingSection = scriptSections.find((s) => s.id === draftingScriptSectionId) || null;
        const ladder = groupScripts(callScripts).flatMap((g) => g.rows);
        return (
          <CallScriptsShell scripts={callScripts} view={scriptsView} setView={setScriptsView}
            error={scriptsError || scriptDraftError}
            onDismissError={() => { clearScriptsError(); setScriptDraftError(null); }}
            menuOpen={menuOpen} setMenuOpen={setMenuOpen} onAdd={addAndOpenScript}
            draftingBack={draftingSection
              ? { label: scriptBackLabel(copyScript), onClick: () => setDraftingScriptSectionId(null) } : null}>
            {scriptsView === "copy" ? (
              <CallScriptsCopy scripts={callScripts} sectionsFor={scriptSectionsFor} selectedId={copyScript?.id}
                onExitToTable={() => setScriptsView("table")}
                onReorder={reorderScripts} onMark={markScript} drafting={!!draftingSection}
                onSelect={(s) => { setDraftingScriptSectionId(null); setOpenScriptId(s.id); }}>
                {draftingSection ? (
                  /* Keyed by the section and the scope, so it holds that
                     section's words from the moment it mounts. */
                  <CallScriptDrafter key={`${draftingSection.id}:${draftingWholeScript ? "whole" : "one"}`}
                    script={copyScript} section={draftingSection} sections={scriptSections}
                    startScope={draftingWholeScript ? "record" : "block"}
                    offers={offers} chatContext={momentumChatContext}
                    onSave={saveScriptSection} onBack={() => setDraftingScriptSectionId(null)}
                    onGoToSection={(id) => { setDraftingWholeScript(false); setDraftingScriptSectionId(id); }}
                    nextRecord={(() => {
                      const i = ladder.findIndex((x) => x.id === copyScript?.id);
                      const next = i >= 0 ? ladder[i + 1] : null;
                      if (!next) return null;
                      return {
                        label: scriptLabel(next), noun: "call script",
                        go: () => {
                          const ss = scriptSectionsFor(next.id);
                          const target = ss.find((x) => sectionWords(x) === 0) || ss[0];
                          setOpenScriptId(next.id);
                          setDraftingScriptSectionId(target ? target.id : null);
                        },
                      };
                    })()} />
                ) : (
                  /* Keyed by the script: its name and sections each hold a
                     draft of their own. */
                  <CallScript key={copyScript?.id} script={copyScript} sections={scriptSections} offers={offers}
                    error={scriptsError} onSave={saveScript} onDelete={askDeleteScript}
                    onSaveSection={saveScriptSection} onRemoveSection={askRemoveScriptSection}
                    onSparkleSection={(sec) => askScriptSparkle(copyScript, sec, scriptSections)}
                    sparklingSectionId={sparklingScriptSectionId} draftedScriptSection={draftedScriptSection}
                    onAddSection={addScriptSection} onReorderSections={reorderScriptSections}
                    onDraftSection={(s) => { setDraftingWholeScript(false); setDraftingScriptSectionId(s.id); }}
                    /* The whole call, landing on the first unwritten section. */
                    onWrite={scriptSections.length ? () => {
                      setDraftingWholeScript(true);
                      setDraftingScriptSectionId((scriptSections.find((x) => sectionWords(x) === 0) || scriptSections[0]).id);
                    } : null} />
                )}
              </CallScriptsCopy>
            ) : (
              <CallScriptsTable scripts={callScripts} sectionsFor={scriptSectionsFor}
                loading={scriptsLoading} onOpen={openScript} onDelete={askDeleteScript}
                onReorder={(from, to, band) => reorderScripts(band, from, to)} />
            )}
          </CallScriptsShell>
        );
      }
      case "newsletter": {
        const nlBlocks = copyNewsletter ? newsletterBlocksFor(copyNewsletter.id) : [];
        const draftingNlBlock = nlBlocks.find((b) => b.id === draftingNewsletterBlockId) || null;
        const nlSource = copyNewsletter ? newsletterSourceOf(copyNewsletter) : null;
        const nlSourceBlocks = nlSource ? newsletterBlocksFor(nlSource.id) : [];
        const nlOrder = groupNewsletters(newsletters).flatMap((g) => g.rows);
        /* WRITE FIRST: a blog post of her own words takes the whole screen
           ("full screen so you could really write"). Keyed by its page, so the
           words load the moment the page exists. */
        /* NOT WHILE SECTIONS ARE LOADING: an older post looks empty until its
           sections arrive, and the writer would give it a blank page. */
        if (newslettersView === "copy" && copyNewsletter && !draftingNlBlock && !newsletterBlocksLoading && isWritingPiece(copyNewsletter, nlBlocks)) {
          const page = nlBlocks.find((b) => b.blockType === "writing");
          return (
            <NewsletterWriter key={`${copyNewsletter.id}:${page?.id || "none"}`}
              newsletter={copyNewsletter} blocks={nlBlocks} newsletters={newsletters}
              offers={offers} pillars={newsletterPillars} error={newslettersError} isMobile={isMobile}
              onBack={() => setNewslettersView("table")}
              onSave={saveNewsletter} onSaveBlock={saveNewsletterBlock}
              onEnsureBlock={(kind, content) => ensureNewsletterBlock(copyNewsletter.id, kind, content)}
              onAskBrill={(args) => askNewsletterShape(copyNewsletter, args)}
              onPublish={isAdmin ? publishBlogPost : null}
              onPreview={isAdmin ? previewNewsletter : null}
              onMakeVariant={makeAndOpenVariant} makingVariant={makingVariant}
              onDelete={askDeleteNewsletter} />
          );
        }
        return (
          <NewsletterShell view={newslettersView} setView={setNewslettersView}
            error={newslettersError || newsletterDraftError}
            onDismissError={() => { clearNewslettersError(); setNewsletterDraftError(null); }}
            menuOpen={menuOpen} setMenuOpen={setMenuOpen} onAdd={addAndOpenNewsletter}
            draftingBack={draftingNlBlock ? { label: PIECE_BACK, onClick: () => setDraftingNewsletterBlockId(null) } : null}>
            {newslettersView === "copy" ? (
              <NewsletterCopy newsletters={newsletters} blocksFor={newsletterBlocksFor}
                onExitToTable={() => setNewslettersView("table")}
                selectedId={copyNewsletter?.id} onReorder={reorderNewsletters} onMark={markNewsletter}
                drafting={!!draftingNlBlock}
                onSelect={(n) => { setDraftingNewsletterBlockId(null); setOpenNewsletterId(n.id); }}>
                {draftingNlBlock ? (
                  /* Keyed by the section and the scope, so it holds that
                     section's words from the moment it mounts. */
                  <NewsletterDrafter key={`${draftingNlBlock.id}:${draftingWholeNewsletter ? "whole" : "one"}`}
                    newsletter={copyNewsletter} block={draftingNlBlock} blocks={nlBlocks}
                    source={nlSource} sourceBlocks={nlSourceBlocks}
                    startScope={draftingWholeNewsletter ? "record" : "block"}
                    offers={offers} chatContext={momentumChatContext}
                    onSave={saveNewsletterBlock} onBack={() => setDraftingNewsletterBlockId(null)}
                    onGoToBlock={(id) => { setDraftingWholeNewsletter(false); setDraftingNewsletterBlockId(id); }}
                    nextRecord={(() => {
                      const i = nlOrder.findIndex((x) => x.id === copyNewsletter?.id);
                      const next = i >= 0 ? nlOrder[i + 1] : null;
                      if (!next) return null;
                      return {
                        label: newsletterLabel(next), noun: "piece",
                        go: () => {
                          const bs = newsletterBlocksFor(next.id);
                          const target = bs.find((x) => nlBlockWords(x) === 0) || bs[0];
                          setOpenNewsletterId(next.id);
                          setDraftingNewsletterBlockId(target ? target.id : null);
                        },
                      };
                    })()} />
                ) : (
                  /* Keyed by the piece: its title and sections each hold a
                     draft of their own. */
                  <NewsletterPiece key={copyNewsletter?.id} newsletter={copyNewsletter} newsletters={newsletters}
                    blocks={nlBlocks} offers={offers} pillars={newsletterPillars} error={newslettersError}
                    onSave={saveNewsletter} onDelete={askDeleteNewsletter}
                    onSaveBlock={saveNewsletterBlock} onRemoveBlock={askRemoveNewsletterBlock}
                    onSparkleBlock={(b) => askNewsletterSparkle(copyNewsletter, b, nlBlocks)}
                    sparklingBlockId={sparklingNewsletterBlockId} draftedBlock={draftedNewsletterBlock}
                    onAddBlock={(n, kind) => addNewsletterBlock(n.id, kind)}
                    onReorderBlocks={reorderNewsletterBlocks}
                    onDraftBlock={(b) => { setDraftingWholeNewsletter(false); setDraftingNewsletterBlockId(b.id); }}
                    onMakeVariant={makeAndOpenVariant} makingVariant={makingVariant}
                    onCopyOut={(text) => navigator.clipboard?.writeText(text)}
                    brand={emailBrand}
                    onPublish={isAdmin ? publishBlogPost : null}
                    /* The whole piece, landing on the first unwritten section. */
                    onWrite={nlBlocks.length ? () => {
                      setDraftingWholeNewsletter(true);
                      setDraftingNewsletterBlockId((nlBlocks.find((x) => nlBlockWords(x) === 0) || nlBlocks[0]).id);
                    } : null} />
                )}
              </NewsletterCopy>
            ) : (
              <NewsletterTable newsletters={newsletters} blocksFor={newsletterBlocksFor}
                loading={newslettersLoading} onOpen={openNewsletter} onDelete={askDeleteNewsletter}
                onReorder={(from, to, band) => reorderNewsletters(band, from, to)} />
            )}
          </NewsletterShell>
        );
      }
      case "website":
        if (websiteView === "copy") {
          return (
            <WebsitePageCopy pages={pages} selectedId={copyPage?.id} onSelect={(p) => { setDraftingSectionId(null); setEditingPage({ ...p }); }}
              onReorder={reorderPages} sectionsFor={sectionsFor} isMobile={isMobile} siteName={siteLabel(site)}
              menuOpen={menuOpen} setMenuOpen={setMenuOpen} onAddStandard={addStandardAndOpen} onAddOther={startAddPage} missing={missingPages}
              standardPages={standardPages} level1={siteSwitcher} drawer={siteDrawer}
              drafting={!!draftingSectionId}
              /* While she is drafting, the bar carries the way back and the page
                 picker — so the drafting screen does not grow a second chrome
                 row under this one. */
              onBackFromDrafting={() => setDraftingSectionId(null)} onSwitchPage={switchPageInChat}
              onBackToSites={() => setWebsiteView("table")} onMark={markPage} screen={pageScreen}>
              <WebsitePage draft={editingPage} setDraft={setEditingPage} pages={pages} outputs={websiteOutputs} onSave={savePage} offers={offers} sites={sites}
                /* Style + GHL prompt are admin-only while they are new, the way
                   Presentations started (docs/sites-pictures-spec.md). */
                onSaveStyle={isAdmin ? savePageStyle : null}
                onDelete={() => askDeletePage(editingPage)}
                /* THE FOOTER GOES UP ONE LEVEL, like the back at the top of the
                   same screen. Her call, 2026-09-18: "let's go back to all
                   pages, she can do it twice." It used to say All sites while
                   the top said All pages, so one screen carried two backs to
                   two different places. Rule 1 spells the footer as
                   `[← All pages]` anyway; All sites was the drift.
                   On a WIDE screen the pages list is the rail, already beside
                   her, so there the footer still leaves the site. */
                onBackToTable={twoPaneHub ? () => setWebsiteView("table") : () => pageScreen.setListOpen(true)}
                backLabel={twoPaneHub ? "All sites" : "All pages"} go={go} isMobile={isMobile} saving={savingPage} embedded
                sections={editingPage ? sectionsFor(editingPage.id) : []} sectionsFor={sectionsFor} onSwitchPage={switchPageInChat}
                sectionsLoading={sectionsLoading} sectionsError={sectionsError} onDismissSectionsError={clearSectionsError}
                onSaveSection={saveSection} onDeleteSection={askDeleteSection} onAddSection={addSection} onMoveSection={moveSection} onReorderSections={reorderSections} onRefreshSections={askRefreshSections}
                onSparkleSection={askSectionSparkle} sparklingSectionId={sparklingSectionId}
                draftedSection={draftedSection}
                draftError={sectionDraftError} onDismissDraftError={() => setSectionDraftError(null)}
                onChangeKind={changeKind} chatContext={momentumChatContext}
                draftingId={draftingSectionId} setDraftingId={setDraftingSectionId}
                draftingWhole={draftingWholePage} setDraftingWhole={setDraftingWholePage} />
            </WebsitePageCopy>
          );
        }
        return (
          <SiteTable sites={sites} pagesOf={pagesOf} sectionsFor={sectionsFor}
            /* The pages have to have arrived before a per-site count can be
               true — until then there is nothing to count, and a table of
               zeroes reads as loss rather than as loading. */
            loading={sitesLoading || pagesLoading}
            error={sitesError || pagesError} onDismissError={() => { clearSitesError(); clearPagesError(); }}
            onOpen={(st) => { setOpenSiteId(st.id); setSiteCog(false); setEditingPage(null); setDraftingSectionId(null); setWebsiteView("copy"); }}
            onDelete={sites.length > 1 ? askDeleteSite : null}
            onReorder={reorderSites}
            menuOpen={menuOpen} setMenuOpen={setMenuOpen}
            addOptions={SITE_KINDS.map((k) => ({ id: k.id, label: k.label, hint: k.hint }))}
            onAdd={async (kind) => {
              const made = await addSite(kind);
              if (made) { setOpenSiteId(made.id); setEditingPage(null); setSiteCog(true); setWebsiteView("copy"); }
            }} />
        );
      /* No separate `website_page` route any more. Editing a page is Page copy
         with that page selected, so there is one editing screen rather than two
         that differ only by whether the page list is beside them. */
      case "social": return <SocialNote />;
      case "library": return <Library isMobile={isMobile} />;
      case "onboarding": return <RunOverview title="Onboarding" desc="One simple flow to bring a new client in the door — not multiple tools." rows={[
        ["Welcome sequence", "Auto-fires on purchase"], ["Intake / pre-work", "Collect what you need before kickoff"], ["Access provisioning", "Portal + community access"],
      ]} note={<><b style={nb}>Automations live inside these steps</b> — the welcome sequence auto-fires. No separate automations bucket needed.</>} />;
      case "client_success": return <RunOverview title="Client Success" desc="The whole journey of an active client — deliver, engage, retain, graduate. One bucket for everything after they're in the door." rows={[
        ["Delivery", "Session scheduler, live-call reminders (WORTHI cadence), homework — where your Course actually gets delivered"],
        ["Community", "Discussion prompts, rituals, health — pending your community platform"],
        ["Wins & testimonials", "Milestone / win capture that feeds your existing testimonial system"],
        ["Offboarding & Alumni", "Completion sequence, case-study ask, alumni handoff, referral ask"],
      ]} note={<><b style={nb}>Wiring note:</b> the Course is built in Build but delivered here — those two need connecting. Automations (week-3 check-in, at-risk flags, renewal triggers) live inside these steps.</>} />;
      case "admin": return <RunOverview title="Admin" desc="The back office — money, numbers, and how you run things." rows={[
        ["Financial", "Connects to your CRM / payment processor (GHL / Stripe) — not a Brill build"],
        ["Reporting & Business Health", "Completion rates, revenue per tier, admin rollup"],
        ["SOPs", "Your documented processes — how the business runs, written down"],
      ]} note={<><b style={nb}>Financial</b> is labeled as an integration, not a coming-soon Brill feature — Brill isn't building billing.</>} />;
      default:
        return soonScreen(active);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, background: B.bg, color: B.text, height: "100%", overflow: "hidden" }}>
      {/* The section bar, shared with Me (components/ChromeBar.jsx) so the two
          cannot drift. The amber rule and megaphone are Momentum's identity,
          brand values rather than the themed amber used in the body. */}
      <ChromeBar icon={Megaphone} title="Momentum" subtitle="Build · Grow · Run" rule="var(--brand-amber)"
        isMobile={isMobile} onMenuOpen={onMenuOpen} onLibrary={() => go("library")} libraryOn={active === "library"} />

      <div style={{ flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: 0 }}>
        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? "20px 16px 44px" : "28px 32px 56px", minWidth: 0 }}>
          {/* ONE container for every Momentum panel — change this and they all
              move together. 860 was a reading width, right for the prose-heavy
              panels and too tight the moment a table carries six columns. Prose
              doesn't sprawl to match, because `pDesc` and `Note` cap themselves
              at a readable measure regardless of what this is. */}
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            {renderPanel()}
          </div>
        </div>
      </div>
      {confirm && <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

// ── Panel components ──────────────────────────────────────────────────────────











/* The Website studio: her real pages, not a fixed list of four names.
   The four pages My Website writes are offered by name (see utils/websitePages.js)
   and once created they
   then belong to her — renameable, deletable, and joined back to the locked copy
   through `sourceSection` rather than through their title.

   A page's copy lives in its SECTIONS — editable blocks, split once from the
   locked My Website copy and hers from then on. See utils/websiteSections.js.
   This table shows the first of them and how many there are; the editing is on
   the page itself. */











/* Page copy: the same editor as clicking into a page, with the pages down the
   side instead of a Back link. Opens on the first page, so writing never starts
   with a choice. The sidebar is also where page order lives — the arrows write
   `sort_order`, which is the order the site itself is in. */


/* ── A typed section ──────────────────────────────────────────────────────────
   A section is one of nine KINDS, each with its own named fields — Banner is
   eyebrow / headline / subhead / button, Services is a heading and a list of
   priced items, Other is the name-and-a-box-of-text it always was. The fields
   come from utils/websiteSectionKinds.js, which is also what the parser reads and
   what Brill is told, so a field added there appears here with no change.

   Why typed at all: this panel's output is a handoff document, and the structure
   IS the deliverable. "Banner → eyebrow / headline / subhead / button" is usable
   by whoever builds the page; a paragraph of prose is a draft.               */





/* One section on a website page.
   It holds its own draft rather than writing through on every keystroke — a
   Supabase round trip per character would be both slow and expensive in writes.
   It commits on blur, and only when something actually changed, so tabbing
   through a page of sections doesn't rewrite every row she didn't touch. */






/* ── Writing a section with Brill ─────────────────────────────────────────────
   The Offer Ladder's drafting chat, scoped to one section of one page: the
   fields on one side, the conversation on the other, and Brill filling the
   fields through a STRUCTURED BLOCK rather than prose we read values out of.
   See utils/sectionBlock.js for why that distinction is load-bearing.

   It takes over the page-copy area rather than opening a third screen. Sections
   are edited in a list; a drill-in would put her two levels from the page she is
   writing, which is what the last round of Website work just undid.

   WHAT BRILL WRITES SAVES ITSELF. Everything on this screen already saves as she
   leaves a field, so holding a drafted block back until she pressed something
   would be the odd behaviour — and losing Brill's draft to a click on Back would
   be worse. She can edit or delete it like anything else she typed. */







/* ── COURSE ───────────────────────────────────────────────────────────────────
   Her programme as ordered MODULES, not dated weeks. She dropped weeks entirely
   (2026-08-17): when a module runs is decided wherever the course is hosted, so
   Brill holds what it says and nothing about when.

   The modules seed themselves once from the Curriculum she locked in My Offer —
   which stays there, because it's what she markets from. Each seeded module
   arrives with its title, outcome and homework already written; the outline,
   questions and pre-work are what she builds here.                             */










/* ── The Momentum edit pattern ─────────────────────────────────────────────────
   Form / Brainstorm is the shape every Momentum tool moves to: a form that never
   writes itself, and a separate mode where chat lives. These are written to be
   reused by Website, Lesson Plans and the rest — keep them tool-agnostic.        */



// A persistent read on where the record stands, so "did I save this?" is answerable
// without scrolling to the Status pills.






// ── Style constants ───────────────────────────────────────────────────────────
